import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Download,
  RefreshCw,
  AlertCircle,
  Calendar,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Repeat,
  Save,
  MapPin,
  Info,
  Truck,
  Wallet,
  CreditCard,
  Bike,
  Car,
  Settings2,
  Gift,
  Receipt,
  Percent,
  ShieldCheck,
  UserCheck,
  Phone,
  ChevronDown,
  ChevronUp,
  History,
  Search,
  RotateCcw,
} from "lucide-react";
import {
  useLazyReviewsPendingByOrderIdQuery,
  useLazyReplacementProductsQuery,
  useSubmitOrderReviewMutation,
  useOrderFullFillUpdateMutation,
  useRefundToCustomerMutation,
  useLazyOrderActivityQuery,
  useReturnOrderMutation,
  useMarkOrderFullyPaidMutation,
} from "../../../redux/actions/Admin/orderAdmin";
import { IRecommendedProduct } from "../../../types/Response/AdminResponse";
import { IOrderFullFIllRequest } from "../../../types/Request/AdminRequest";
import { orderActivity } from "../../../types/Response/AdminResponse";
import { ImagePath } from "../../../assests";

interface OrderDetailDrawerProps {
  orderId: number;
  onClose: () => void;
  onRefreshDashboard: () => void;
}

type ItemStatus = "Accept" | "Reject";
type RejectMode = "Full" | "Replace";

interface LocalItemState {
  orderItemId: number;
  itemType: string;
  youSaved: number
  purchaseAmount?: number;
  productId: number | null;
  productVariantId: number | null;
  productName: string;
  originalQty: number;
  originalPrice: number;
  originalUnitPrice: number;
  totalPrice: number;
  status: ItemStatus;
  rejectQty: number;
  rejectMode: RejectMode;
  reason?: string;
  replacementProducts: IRecommendedProduct[];
  selectedReplacements: { product: IRecommendedProduct; quantity: number }[];
  isSubscription: boolean;
  billingCycle: string | null;
  billingInterval: number | null;
  replacementSearchTerm: string;
}

const OrderDetailDrawer: React.FC<OrderDetailDrawerProps> = ({
  orderId,
  onClose,
  onRefreshDashboard,
}) => {
  const [activeTab, setActiveTab] = useState<"items" | "activity" | "refunds">(
    "items",
  );
  const [isCompilingInvoice, setIsCompilingInvoice] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState("");
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // Accordion open/close state for items tab revision history
  const [isReviewHistoryOpen, setIsReviewHistoryOpen] = useState(true);
  const [isReturnHistoryOpen, setIsReturnHistoryOpen] = useState(true);
  const [fullPaymentReference, setFullPaymentReference] = useState("");

  // Settlement & Refund States
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [editableSettlementAmount, setEditableSettlementAmount] = useState("");
  const [refundConfirmation, setRefundConfirmation] = useState<{
    amount: number;
    reason: string;
    warnings: string[];
  } | null>(null);
  const [revisionItems, setRevisionItems] = useState<LocalItemState[]>([]);
  const [selectedRefundMethod, setSelectedRefundMethod] = useState<
    "Wallet" | "OriginalPayment"
  >("Wallet");
  const [nowTick, setNowTick] = useState(Date.now());

  // Return Order States
  const [returnItemsSelection, setReturnItemsSelection] = useState<
    Record<number, { selected: boolean; quantity: number; reason: string }>
  >({});

  const [returnOrder, { isLoading: isReturnSubmitting }] =
    useReturnOrderMutation();

  const [refundToCustomer, { isLoading: isRefundSubmitting }] =
    useRefundToCustomerMutation();

  const [selectedProvider, setSelectedProvider] = useState<string | null>(
    "Manual",
  );
  const [fulfillForm, setFulfillForm] = useState<IOrderFullFIllRequest>({
    status: "" as any,
    provider: "Manual",
    vehicleType: "bike",
    courierName: "",
    trackingNumber: "",
    remarks: "",
    statusDate: new Date().toISOString(),
  });

  const [
    triggerOrderActivity,
    { data: activityData, isFetching: isActivityLoading },
  ] = useLazyOrderActivityQuery();

  const [
    triggerReviewsPending,
    { data: reviewDetails, isFetching: isReviewDetailsLoading },
  ] = useLazyReviewsPendingByOrderIdQuery();

  const [submitOrderReview, { isLoading: isSubmittingReview }] =
    useSubmitOrderReviewMutation();
  const [fetchReplacements] = useLazyReplacementProductsQuery();
  const [fulfillOrderUpdate, { isLoading: isFulfillSubmitting }] =
    useOrderFullFillUpdateMutation();
  const [
    markOrderFullyPaid,
    { isLoading: isMarkingFullPayment },
  ] = useMarkOrderFullyPaidMutation();


  const showNotification = (msg: string, type: "success" | "error") => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  useEffect(() => {
    if (activeTab === "activity") triggerOrderActivity(orderId);
  }, [activeTab, orderId]);

  useEffect(() => {
    if (orderId) {
      triggerReviewsPending(orderId);
    }
  }, [orderId]);

  useEffect(() => {
    if (reviewDetails && reviewDetails.items) {
      const initializedRows: LocalItemState[] =
        reviewDetails.items.map((item) => {
          const isPet = item.itemType === "Pet";

          const petPurchaseAmount = Number(
            (item as any).purchaseAmount ?? item.unitSalePrice ?? 0,
          );

          const normalSalePrice = Number(
            item.unitSalePrice > 0 ? item.unitSalePrice : item.unitPrice,
          );

          const normalUnitPrice = Number(
            (item as any).unitPrice ?? item.unitSalePrice ?? 0,
          );

          return {
            orderItemId: item.orderItemId,
            itemType: item.itemType,
            productId: item.productId,
            productVariantId: item.productVariantId,
            productName: item.name,
            youSaved: item.youSaved,
            originalQty: item.quantity,
            totalPrice: item.totalPrice,

            // PET:
            // FullPayment => purchaseAmount = full payment amount
            // PartialBooking => booking amount paid now
            // Keep this separate from originalPrice so the UI shows the
            // actual pet purchase/payment amount instead of the list price.
            purchaseAmount: isPet
              ? petPurchaseAmount
              : undefined,

            // PRODUCT/SERVICE:
            // keep existing unitSalePrice behavior
            originalPrice: isPet
              ? item.unitSalePrice ?? item.unitPrice
              : normalSalePrice,

            originalUnitPrice: isPet
              ? item.unitSalePrice ?? item.unitPrice
              : normalUnitPrice,

            status: "Accept",
            rejectQty: item.quantity,
            rejectMode: "Full",
            reason: "",
            replacementProducts: [],
            selectedReplacements: [],
            isSubscription: item.isSubscription,
            billingCycle: item.billingCycle,
            billingInterval: item.billingInterval,
            replacementSearchTerm: "",
          };
        });

      setRevisionItems(initializedRows);

      // Initialize Return Selection Map
      const initialReturns: Record<
        number,
        { selected: boolean; quantity: number; reason: string }
      > = {};
      reviewDetails.items.forEach((item) => {
        initialReturns[item.orderItemId] = {
          selected: false,
          quantity: item.quantity,
          reason: "",
        };
      });
      setReturnItemsSelection(initialReturns);

      const orderTotal =
        reviewDetails.pricing?.totalAmount ?? reviewDetails.pricing?.total ?? 0;
      const differenceAmount = Math.abs(
        Number(reviewDetails.currentRevision?.differenceAmount || 0),
      );

      const defaultAmount =
        differenceAmount > 0 ? differenceAmount : orderTotal;
      setEditableSettlementAmount(
        defaultAmount > 0 ? defaultAmount.toFixed(2) : "",
      );
    }
  }, [reviewDetails]);

  useEffect(() => {
    if (!reviewDetails?.availableActions?.hasPendingRevision) return;
    const interval = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [reviewDetails?.availableActions?.hasPendingRevision]);

  useEffect(() => {
    setSelectedProvider("Manual");
    setFulfillForm({
      status: "" as any,
      provider: "Manual",
      vehicleType: "bike",
      courierName: "",
      trackingNumber: "",
      remarks: "",
      statusDate: new Date().toISOString(),
    });
  }, [orderId]);

  const activeSelectedProviderObj = useMemo(() => {
    const providers =
      reviewDetails?.availableActions?.availableFulfillmentProviders;
    return providers?.find((p) => p.selected || p.enabled) || null;
  }, [reviewDetails]);

  const isProviderLocked = !!activeSelectedProviderObj?.selected;

  useEffect(() => {
    if (!activeSelectedProviderObj) return;

    const validStatuses = [
      "Packed",
      "Shipped",
      "Delivered",
      "Cancelled",
      "Refund",
      "Return",
    ];
    const headerStatus = reviewDetails?.header?.orderStatus;

    setSelectedProvider(activeSelectedProviderObj.provider);
    setFulfillForm((prev) => ({
      ...prev,
      provider: activeSelectedProviderObj.provider,
      courierName: activeSelectedProviderObj.courierName || prev.courierName,
      trackingNumber:
        activeSelectedProviderObj.trackingNumber || prev.trackingNumber,
      vehicleType:
        (activeSelectedProviderObj.vehicleType as IOrderFullFIllRequest["vehicleType"]) ||
        prev.vehicleType,
      status: validStatuses.includes(headerStatus as string)
        ? (headerStatus as IOrderFullFIllRequest["status"])
        : prev.status,
    }));
  }, [activeSelectedProviderObj, reviewDetails]);

  const updateItem = (itemIdx: number, patch: Partial<LocalItemState>) => {
    setRevisionItems((prev) => {
      const copy = [...prev];
      copy[itemIdx] = { ...copy[itemIdx], ...patch };
      return copy;
    });
  };

  const handleToggleReject = (itemIdx: number) => {
    const currentItem = revisionItems[itemIdx];
    const nextStatus = currentItem.status === "Reject" ? "Accept" : "Reject";

    updateItem(itemIdx, {
      status: nextStatus,
      ...(nextStatus === "Accept"
        ? {
          rejectMode: "Full",
          rejectQty: currentItem.originalQty,
          reason: "",
          selectedReplacements: [],
          replacementProducts: [],
          replacementSearchTerm: "",
        }
        : {}),
    });
  };

  const handleSetRejectMode = async (itemIdx: number, mode: RejectMode) => {
    updateItem(itemIdx, { rejectMode: mode });
    if (mode === "Replace") {
      const item = revisionItems[itemIdx];
      try {
        const res = await fetchReplacements({
          orderId,
          orderItemId: item.orderItemId,
          search: "",
        }).unwrap();
        updateItem(itemIdx, { replacementProducts: res || [] });
      } catch (err) {
        showNotification("Failed to fetch suggested products.", "error");
      }
    }
  };
  const handleMarkFullPayment = async () => {
    if (!orderId) {
      showNotification("Invalid order.", "error");
      return;
    }

    if (Number(p?.remainingAmount ?? 0) <= 0) {
      showNotification("No remaining amount for this order.", "error");
      return;
    }


    try {
      await markOrderFullyPaid({
        orderId,
        comment: fullPaymentReference.trim() || "Fully Paid",
      }).unwrap();

      showNotification(
        "Remaining payment marked as paid successfully.",
        "success",
      );

      setFullPaymentReference("");

      await triggerReviewsPending(orderId);
      onRefreshDashboard();
    } catch (err: any) {
      showNotification(
        err?.data?.Message ||
        err?.data?.message ||
        "Failed to mark remaining payment as paid.",
        "error",
      );
    }
  };

  const toggleSelectReplacementProduct = (
    itemIdx: number,
    targetProd: IRecommendedProduct,
  ) => {
    const currentSelected = [...revisionItems[itemIdx].selectedReplacements];
    const matchIdx = currentSelected.findIndex(
      (x) => x.product.productId === targetProd.productId,
    );

    if (matchIdx > -1) {
      currentSelected.splice(matchIdx, 1);
    } else {
      currentSelected.push({ product: targetProd, quantity: 1 });
    }
    updateItem(itemIdx, { selectedReplacements: currentSelected });
  };

  const updateReplacementQuantity = (
    itemIdx: number,
    prodId: number,
    qty: number,
  ) => {
    const currentSelected = [...revisionItems[itemIdx].selectedReplacements];
    const matchIdx = currentSelected.findIndex(
      (x) => x.product.productId === prodId,
    );
    if (matchIdx > -1) {
      currentSelected[matchIdx].quantity = Math.max(1, qty);
      updateItem(itemIdx, { selectedReplacements: currentSelected });
    }
  };

  const handleDownloadInvoice = () => {
    if (!reviewDetails) return;

    const invoiceWindow = window.open("", "_blank");
    if (!invoiceWindow) {
      alert("Please allow popups to download the invoice.");
      return;
    }

    const { header, customer, address, pricing, items = [] } = reviewDetails;
    const orderNumber = header?.orderNumber ?? "N/A";
    const orderDate = header?.orderDate;
    const currency = header?.currency ?? "AED";

    const formattedOrderDate = orderDate
      ? new Date(orderDate).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
      : "N/A";

    const displayStatus = header?.orderStatus ?? "Confirmed";
    const activeInvoiceItems = items.filter((item: any) => item.quantity > 0);

    const itemsHtml = activeInvoiceItems
      .map(
        (item: any) => `
      <tr>
        <td class="item-column">
          <div class="item-wrapper">
            ${item.imageUrl
            ? `<img src="${item.imageUrl}" class="item-thumb" alt="${item.name}" />`
            : `<div class="item-thumb-placeholder">No Img</div>`
          }

            <div>
              <div class="item-name">
                ${item.name ?? "N/A"}
              </div>

              ${item.subtitle
            ? `<div class="item-subtitle">${item.subtitle}</div>`
            : ""
          }
            </div>
          </div>
        </td>

        <td class="type-column">
          <span class="badge badge-type">
            ${item.itemType ?? "Product"}
          </span>

          ${item.itemType === "Pet" && item.purchaseType
            ? `
                <div
                  style="
                    margin-top: 4px;
                    font-size: 9px;
                    font-weight: 700;
                    color: #7c3aed;
                  "
                >
                  ${item.purchaseType === "PartialBooking"
              ? "Partial Booking"
              : item.purchaseType === "FullPayment"
                ? "Full Payment"
                : item.purchaseType
            }
                </div>
              `
            : ""
          }
        </td>

        <td class="quantity-column text-center">
          ${item.quantity ?? 0}
        </td>

        <td class="amount-column text-right">

          ${item.isFreeItem
            ? `<span class="free-item">FREE</span>`
            : item.itemType === "Pet"
              ? `
                    <div
                      style="
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                        gap: 3px;
                      "
                    >

                      <!-- PET TOTAL PRICE -->
                      <div
                        style="
                          font-size: 11px;
                          font-weight: 700;
                          color: #1e293b;
                        "
                      >
                        Total Price:
                        ${currency}
                        ${Number(
                item.unitSalePrice ??
                item.unitPrice ??
                0
              ).toFixed(2)}
                      </div>

                      <!-- PET PAID / BOOKING AMOUNT -->
                      ${item.purchaseAmount !== undefined &&
                item.purchaseAmount !== null
                ? `
                              <div
                                style="
                                  font-size: 10px;
                                  font-weight: 600;
                                  color: #475569;
                                "
                              >
                                Paid / Booking:
                                ${currency}
                                ${Number(
                  item.purchaseAmount
                ).toFixed(2)}
                              </div>
                            `
                : ""
              }

                      <!-- PET REMAINING AMOUNT -->
                      ${item.remainingAmount !== undefined &&
                item.remainingAmount !== null &&
                Number(item.remainingAmount) > 0
                ? `
                              <div
                                style="
                                  margin-top: 2px;
                                  padding-top: 3px;
                                  border-top: 1px dashed #f59e0b;
                                  font-size: 10px;
                                  font-weight: 700;
                                  color: #b45309;
                                "
                              >
                                Remaining:
                                ${currency}
                                ${Number(
                  item.remainingAmount
                ).toFixed(2)}
                              </div>
                            `
                : ""
              }

                    </div>
                  `
              : `
                    ${currency}
                    ${Number(
                item.totalPrice ??
                item.unitSalePrice * item.quantity
              ).toFixed(2)}
                  `
          }

        </td>
      </tr>
    `,
      )
      .join("");

    const watermarkImage =
      ImagePath.Petland_Watermark?.default ?? ImagePath.Petland_Watermark;
    const petlandLogo =
      ImagePath.Petland_Watermark2?.default ?? ImagePath.Petland_Watermark2;
    console.log("ORDER REVIEW DEBUG", {
      orderId,
      hasPendingRevision,
      isExpired,
      isPendingSettlement,
      canReject,
      canProceedToFulfillment,
    });
    const invoiceHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Invoice - ${orderNumber}</title>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #1e293b; background: #ffffff; font-size: 12px; }
          .page { width: 100%; max-width: 794px; margin: 0 auto; padding: 30px 34px; background: #ffffff; position: relative; }
          .invoice-container { width: 100%; position: relative; z-index: 1; }
          .invoice-watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 360px; max-width: 50%; height: auto; opacity: 0.08; z-index: 0; pointer-events: none; object-fit: contain; }
          .invoice-header { width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 30px; padding-bottom: 16px; border-bottom: 2px solid #6DBE45; position: relative; z-index: 2; min-height: 90px; }
          .invoice-logo-wrapper { flex-shrink: 0; }
          .invoice-logo { display: block; width: 160px; height: auto; object-fit: contain; }
          .brand-subtitle { margin-top: 3px; font-size: 10px; color: #64748b; }
          .invoice-title { flex: 1; text-align: right; }
          .invoice-title h1 { margin: 0; font-size: 22px; font-weight: 700; color: #0f172a; }
          .invoice-title p { margin: 3px 0 0; font-size: 11px; color: #64748b; }
          .invoice-info { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 20px; position: relative; z-index: 2; }
          .info-card { padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
          .section-title { margin-bottom: 8px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
          .info-row { margin-bottom: 4px; font-size: 11px; line-height: 1.4; color: #334155; }
          .info-label { font-weight: 600; color: #475569; }
          .order-summary { width: 100%; margin-top: 16px; padding: 10px 14px; border: 1px solid #e2e8f0; border-radius: 8px; position: relative; z-index: 2; }
          .order-summary-row { display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #475569; }
          .order-status { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 999px; background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d !important; font-size: 10px; font-weight: 700 !important; }
          .items-table { width: 100%; margin-top: 16px; border-collapse: collapse; table-layout: fixed; position: relative; z-index: 2; }
          .items-table th { padding: 8px 10px; background: #f8fafc; text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; }
          .items-table td { padding: 8px 10px; font-size: 11px; vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
          .item-wrapper { display: flex; align-items: center; gap: 10px; }
          .item-thumb { width: 38px; height: 38px; object-fit: cover; border-radius: 6px; border: 1px solid #e2e8f0; flex-shrink: 0; }
          .item-thumb-placeholder { width: 38px; height: 38px; background: #f1f5f9; color: #94a3b8; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 8px; font-weight: 600; flex-shrink: 0; }
          .item-name { font-weight: 600; color: #1e293b; }
          .item-subtitle { margin-top: 2px; font-size: 9px; color: #94a3b8; }
          .badge { display: inline-block; padding: 1px 5px; border-radius: 4px; font-size: 8px; font-weight: 700; text-transform: uppercase; margin-right: 4px; }
          .badge-type { background: #f1f5f9; color: #475569; }
          .free-item { font-weight: 700; color: #15803d; }
          .text-center { text-align: center !important; }
          .text-right { text-align: right !important; }
          .payment-summary { width: 100%; margin-top: 20px; padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 8px; position: relative; z-index: 2; }
          .payment-summary .section-title { margin-bottom: 8px; padding-bottom: 7px; border-bottom: 1px solid #e2e8f0; }
          .payment-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 11px; color: #475569; }
          .payment-row span:last-child { font-weight: 600; color: #1e293b; text-align: right; }
          .payment-row.total { margin-top: 6px; padding-top: 8px; border-top: 2px solid #e2e8f0; font-size: 13px; font-weight: 700; color: #2F5E2E; }
          .payment-row.total span { color: #2F5E2E; font-weight: 700; }
          .invoice-footer { width: 100%; margin-top: 26px; padding-top: 12px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 10px; line-height: 1.5; color: #94a3b8; position: relative; z-index: 2; }
          .invoice-footer strong { color: #2F5E2E; font-size: 11px; }
          @page { size: A4; margin: 0; }
          @media print {
            html, body { width: 100%; margin: 0 !important; padding: 0 !important; background: #ffffff; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .page { width: 100%; max-width: none; margin: 0; padding: 10mm; }
            .invoice-watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 320px; opacity: 0.08; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="invoice-container">
            <img src="${watermarkImage}" class="invoice-watermark" alt="PetLand Watermark" />
            <div class="invoice-header">
              <div class="invoice-logo-wrapper">
                <img src="${petlandLogo}" class="invoice-logo" alt="PetLand Logo" />
                <div class="brand-subtitle">More than a Pet Store</div>
                <div class="brand-subtitle">TNR: 100502238700003</div>
                <div class="brand-subtitle">Mobile: 043372862</div>
              </div>
              <div class="invoice-title">
                <h1>INVOICE</h1>
                <p>Order #${orderNumber}</p>
                <p>${formattedOrderDate}</p>
              </div>
            </div>

            <div class="invoice-info">
              <div class="info-card">
                <div class="section-title">Bill To</div>
                <div class="info-row"><span class="info-label">Name:</span> ${customer?.name ?? "N/A"
      }</div>
                <div class="info-row"><span class="info-label">Email:</span> ${customer?.email ?? "N/A"
      }</div>
                <div class="info-row"><span class="info-label">Mobile:</span> ${customer?.mobile ?? address?.phoneNumber ?? "N/A"
      }</div>
              </div>
              <div class="info-card">
                <div class="section-title">Delivery Address</div>
                <div class="info-row"><span class="info-label">Name:</span> ${address?.fullName ?? customer?.name ?? "N/A"
      }</div>
                ${address?.phoneNumber
        ? `<div class="info-row"><span class="info-label">Mobile:</span> ${address.phoneNumber}</div>`
        : ""
      }
                ${address?.addressLine1
        ? `<div class="info-row">${address.addressLine1}</div>`
        : ""
      }
                ${address?.addressLine2
        ? `<div class="info-row">${address.addressLine2}</div>`
        : ""
      }
                <div class="info-row">${[
        address?.city,
        address?.state,
        address?.postalCode,
      ]
        .filter(Boolean)
        .join(", ")}</div>
                ${address?.country
        ? `<div class="info-row">${address.country}</div>`
        : ""
      }
              </div>
            </div>

            <div class="order-summary">
              <div class="order-summary-row">
                <span class="info-label">Order Status</span>
                <strong class="order-status">${displayStatus}</strong>
              </div>
            </div>

            <table class="items-table">
              <colgroup>
                <col style="width: 52%;" />
                <col style="width: 16%;" />
                <col style="width: 14%;" />
                <col style="width: 18%;" />
              </colgroup>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Type</th>
                  <th class="text-center">Quantity</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
            </table>

            <div class="payment-summary">
              <div class="section-title">Payment Summary</div>
              <div class="payment-row">
                <span>Items Subtotal</span>
                <span>${currency} ${Number(pricing?.subTotal ?? pricing?.productTotal ?? 0).toFixed(2)}</span>
              </div>
              ${(pricing?.couponDiscount ?? pricing?.discount ?? 0) > 0
        ? `<div class="payment-row"><span>Discount ${pricing?.couponCode ? `(${pricing.couponCode})` : ""
        }</span><span style="color: #dc2626;">-${currency} ${Number(
          pricing?.couponDiscount ?? pricing?.discount,
        ).toFixed(2)}</span></div>`
        : ""
      }
              ${(pricing?.rewardPointDiscount ?? 0) > 0
        ? `<div class="payment-row"><span>Reward Points Disc.</span><span style="color: #dc2626;">-${currency} ${Number(
          pricing.rewardPointDiscount,
        ).toFixed(2)}</span></div>`
        : ""
      }
              ${(pricing?.walletAmountUsed ?? 0) > 0
        ? `<div class="payment-row"><span>Wallet Paid</span><span style="color: #dc2626;">-${currency} ${Number(
          pricing.walletAmountUsed,
        ).toFixed(2)}</span></div>`
        : ""
      }
              ${(pricing?.vatAmount ?? pricing?.tax ?? 0) > 0
        ? `<div class="payment-row"><span>VAT (Tax)</span><span>${currency} ${Number(
          pricing?.vatAmount ?? pricing?.tax,
        ).toFixed(2)}</span></div>`
        : ""
      }
              <div class="payment-row">
                <span>Delivery Fee</span>
                <span>${(pricing?.deliveryFee ?? pricing?.shipping ?? 0) > 0
        ? `${currency} ${Number(pricing?.deliveryFee ?? pricing?.shipping).toFixed(2)}`
        : "Free"
      }</span>
              </div>
              ${(pricing?.platformFee ?? 0) > 0
        ? `<div class="payment-row"><span>Platform Fee</span><span>${currency} ${Number(
          pricing.platformFee,
        ).toFixed(2)}</span></div>`
        : ""
      }
      ${(pricing?.remainingAmount ?? 0) > 0
        ? `<div
      class="payment-row"
      style="
        margin-top: 6px;
        padding-top: 6px;
        border-top: 1px dashed #f59e0b;
        color: #b45309;
        font-weight: 700;
      "
    >
      <span>
        Remaining Amount
      </span>

      <span>
        ${currency}
        ${Number(
          pricing.remainingAmount,
        ).toFixed(2)}
      </span>
    </div>`
        : ""
      }
              <div class="payment-row total">
                <span>Total Amount</span>
                <span>${currency} ${Number(pricing?.totalAmount ?? pricing?.total ?? 0).toFixed(2)}</span>
              </div>
              
              ${(pricing?.gatewayAmount ?? 0) > 0
        ? `<div class="payment-row" style="margin-top: 4px; font-weight: bold; color: #1d4ed8;">
                      <span>Revised Total</span>
                      <span>${currency} ${Number(pricing.gatewayAmount).toFixed(2)}</span>
                    </div>`
        : ""
      }
            </div>

            <div class="invoice-footer">
              <strong>Thank you for shopping with PetLand!</strong><br />
              This invoice is system generated.<br />
           Note: All pets are non-refundable. No returns or exchanges will be accepted on pets or products. Deposits and booking amounts are also non-refundable.
            </div>
          </div>
        </div>
        <script>
          window.onload = function () {
            setTimeout(function () { window.print(); }, 500);
          };
        </script>
      </body>
    </html>
    `;

    invoiceWindow.document.open();
    invoiceWindow.document.write(invoiceHtml);
    invoiceWindow.document.close();
  };

  const acceptedCount = revisionItems.filter(
    (i) => i.status === "Accept",
  ).length;
  const rejectedCount = revisionItems.filter(
    (i) => i.status === "Reject",
  ).length;

  const hasPendingRevision =
    reviewDetails?.availableActions?.hasPendingRevision;
  const canApprove = reviewDetails?.availableActions?.canApprove ?? true;
  const canReject = reviewDetails?.availableActions?.canReject ?? true;
  const canRequestRevision =
    reviewDetails?.availableActions?.canRequestRevision ?? true;
  const canProceedToFulfillment =
    reviewDetails?.availableActions?.canProceedToFulfillment ?? false;

  const currentRevision = reviewDetails?.currentRevision as any;

  const isPaymentPending =
    currentRevision?.status === "PendingSettlement" &&
    currentRevision?.paymentStatus === "PaymentPending";

  const isRefundPending =
    currentRevision?.status === "PendingSettlement" &&
    currentRevision?.paymentStatus === "RefundPending";

  const isPendingSettlement = isPaymentPending || isRefundPending;

  const orderStatus = reviewDetails?.header?.orderStatus || "";
  const paymentStatus = reviewDetails?.header?.paymentStatus || "";

  const isCancelled = orderStatus === "Cancelled";

  const isAlreadyRefunded =
    paymentStatus === "Refunded" ||
    paymentStatus === "PartiallyRefunded" ||
    paymentStatus.toLowerCase().includes("refund");

  const isUnderReview = canApprove || canReject;

  const showFulfillmentPanel = activeTab === "items";

  const pendingReview =
    reviewDetails?.reviewHistory?.find(
      (r: any) =>
        r.hasRevision &&
        r.revisionId === reviewDetails?.availableActions?.pendingRevisionId,
    ) || reviewDetails?.reviewHistory?.[0];

  const expiresOnMs = pendingReview?.expiresOn
    ? new Date(pendingReview.expiresOn).getTime()
    : null;
  const remainingMs = expiresOnMs ? expiresOnMs - nowTick : null;

  const isExpired =
    pendingReview?.isExpired ||
    reviewDetails?.currentRevision?.nextAction === "Expired" ||
    (remainingMs !== null && remainingMs <= 0);

  const getRevisionEntryForItem = (item: LocalItemState) => {
    const rev = reviewDetails?.currentRevision as any;
    if (!rev) return null;
    if (item.itemType === "Product") {
      return (
        rev.products?.find((p: any) => p.orderItemId === item.orderItemId) ||
        null
      );
    }
    if (item.itemType === "Pet") {
      return (
        rev.pets?.find((p: any) => p.orderPetItemId === item.orderItemId) ||
        null
      );
    }
    if (item.itemType === "Service") {
      return (
        rev.services?.find(
          (s: any) => s.orderServiceItemId === item.orderItemId,
        ) || null
      );
    }
    return null;
  };

  const getRevisionOutcome = (item: LocalItemState) => {
    const revEntry: any = getRevisionEntryForItem(item);
    if (!revEntry) return { label: "Accepted", tone: "accept" as const };

    if (item.itemType === "Pet" || item.itemType === "Service") {
      return revEntry.remove
        ? { label: "Removed", tone: "reject" as const }
        : { label: "Accepted", tone: "accept" as const };
    }

    const isReplaced =
      Array.isArray(revEntry.replacements) && revEntry.replacements.length > 0;
    const isRemoved = !isReplaced && (revEntry.remainingQuantity ?? 0) === 0;
    const isPartial =
      !isReplaced &&
      !isRemoved &&
      (revEntry.remainingQuantity ?? item.originalQty) < item.originalQty;

    if (isReplaced) return { label: "Replaced", tone: "replace" as const };
    if (isRemoved) return { label: "Removed", tone: "reject" as const };
    if (isPartial)
      return {
        label: `Qty ${revEntry.remainingQuantity}`,
        tone: "partial" as const,
      };
    return { label: "Accepted", tone: "accept" as const };
  };

  const formatCountdown = (ms: number) => {
    if (ms <= 0) return "Expired";
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h > 0 ? `${h}h ` : ""}${m}m ${s}s`;
  };

  const derivedActionInfo = useMemo(() => {
    if (revisionItems.length === 0)
      return { action: "Approve", label: "Send", needsRemarks: false };

    const allFullyRejected = revisionItems.every(
      (i) =>
        i.status === "Reject" &&
        i.rejectMode === "Full" &&
        i.rejectQty === i.originalQty,
    );
    if (allFullyRejected) {
      return { action: "Reject", label: "Reject Order", needsRemarks: true };
    }

    return {
      action: "RequestRevision",
      label: "Send",
      needsRemarks: true,
    };
  }, [revisionItems]);

  const showSaveBar =
    activeTab === "items" &&
    rejectedCount > 0 &&
    (!hasPendingRevision || isExpired) &&
    !canProceedToFulfillment &&
    !isPendingSettlement;

  const handleSaveOrder = async () => {
    const { action, needsRemarks } = derivedActionInfo;

    if (needsRemarks && !adminRemarks.trim()) {
      showNotification(
        "Please enter a remark explaining the changes.",
        "error",
      );
      return;
    }

    const revisedItemsPayload: any[] = [];
    const petsPayload: any[] = [];
    const servicesPayload: any[] = [];
    const subscriptionsPayload: any[] = [];

    for (const item of revisionItems) {
      const isRejected = item.status === "Reject";
      const remainingQty = isRejected
        ? Math.max(0, item.originalQty - item.rejectQty)
        : item.originalQty;
      const itemReason = isRejected ? item.reason || adminRemarks : null;

      if (item.isSubscription && isRejected && item.rejectMode === "Replace") {
        item.selectedReplacements.forEach((rep) => {
          subscriptionsPayload.push({
            orderSubscriptionId: item.orderItemId,
            productVariantId: rep.product.productvariantId,
            billingCycle: item.billingCycle || "Monthly",
            billingInterval: item.billingInterval || 1,
            reason: itemReason,
          });
        });
      }

      if (item.itemType === "Product") {
        const replacementItems =
          isRejected && item.rejectMode === "Replace"
            ? item.selectedReplacements.map((rep) => ({
              productId: rep.product.productId,
              productVariantId: rep.product.productvariantId,
              quantity: rep.quantity,
              unitSalePrice: rep.product.price,
            }))
            : [];

        revisedItemsPayload.push({
          orderItemId: item.orderItemId,
          remainingQuantity: remainingQty,
          unitSalePrice: item.originalPrice,
          reason: itemReason,
          replacements: replacementItems,
        });
      } else if (item.itemType === "Pet") {
        petsPayload.push({
          orderPetItemId: item.orderItemId,
          remove:
            isRejected && item.rejectMode === "Full" && remainingQty === 0,
          reason: itemReason,
        });
      } else if (item.itemType === "Service") {
        servicesPayload.push({
          orderServiceItemId: item.orderItemId,
          remove:
            isRejected && item.rejectMode === "Full" && remainingQty === 0,
          unitSalePrice: item.originalPrice,
          reason: itemReason,
        });
      }
    }

    const finalPayload = {
      orderId,
      action,
      adminRemarks,
      revisedItems: revisedItemsPayload,
      pets: petsPayload,
      services: servicesPayload,
      subscriptions: subscriptionsPayload,
      updateOrderStatus: true,
      newOrderStatus: action === "Approve" ? "Confirmed" : "Pending",
    };

    try {
      await submitOrderReview({ orderId, data: finalPayload }).unwrap();
      showNotification("Order review saved successfully.", "success");
      setAdminRemarks("");
      await triggerReviewsPending(orderId);
      onRefreshDashboard();
      if (action === "Approve" || action === "Reject") {
        setTimeout(() => onClose(), 1200);
      }
    } catch (err) {
      showNotification("Could not save order changes.", "error");
    }
  };

  const processRefund = async (
    amount: number,
    reason: string,
    confirmWarnings = false,
  ) => {
    const response = await refundToCustomer({
      orderId,
      data: {
        amount,
        refundMethod: selectedRefundMethod,
        reason,
        confirmWarnings,
      },
    }).unwrap();

    // Backend is asking admin to confirm the warnings
    if (response?.requiresConfirmation) {
      setRefundConfirmation({
        amount,
        reason,
        warnings: response.warnings || [],
      });

      return false;
    }

    // Actual successful refund
    if (response?.success) {
      return true;
    }

    return false;
  };

  const handleProcessSettlementRefund = async (
    customReason?: string,
    confirmWarnings = false,
  ) => {
    const finalAmount = parseFloat(editableSettlementAmount);

    if (isNaN(finalAmount) || finalAmount <= 0) {
      showNotification("Please enter a valid amount.", "error");
      return;
    }

    try {
      const reason = customReason || "Revision / Cancelled Settlement Refund";

      const success = await processRefund(finalAmount, reason, confirmWarnings);

      if (!success) {
        return;
      }

      showNotification("Refund processed successfully.", "success");

      setRefundConfirmation(null);

      await triggerReviewsPending(orderId);
      onRefreshDashboard();
    } catch {
      showNotification("Failed to process refund.", "error");
    }
  };

  const handleProcessCustomRefund = async (
    e: React.FormEvent,
    confirmWarnings = false,
  ) => {
    e.preventDefault();

    const finalAmount = parseFloat(refundAmount);

    if (isNaN(finalAmount) || finalAmount <= 0 || !refundReason.trim()) {
      showNotification("Please enter a valid amount and reason.", "error");
      return;
    }

    try {
      const success = await processRefund(
        finalAmount,
        refundReason,
        confirmWarnings,
      );

      if (!success) {
        return;
      }

      showNotification("Custom refund processed successfully.", "success");

      setRefundAmount("");
      setRefundReason("");
      setRefundConfirmation(null);

      await triggerReviewsPending(orderId);
      onRefreshDashboard();
    } catch {
      showNotification("Failed to process refund.", "error");
    }
  };
  // Main Fulfillment Save Logic
  const handleSaveFulfillment = async () => {
    const isSlider = fulfillForm.provider === "Slider";

    //--------------------------------------------------
    // Manual fulfillment requires status.
    // Slider status is controlled by Slider webhooks.
    //--------------------------------------------------

    if (!isSlider && !fulfillForm.status) {
      showNotification("Please select an order status.", "error");
      return;
    }

    //--------------------------------------------------
    // Return
    //--------------------------------------------------

    if (fulfillForm.status === "Return") {
      const selectedReturnItems = Object.entries(returnItemsSelection)
        .filter(([_, value]) => value.selected)
        .map(([orderItemId, value]) => ({
          orderItemId: Number(orderItemId),
          quantity: value.quantity,
          reason:
            value.reason || fulfillForm.remarks || "Item returned by admin",
        }));

      if (selectedReturnItems.length === 0) {
        showNotification("Please select at least one item to return.", "error");
        return;
      }

      try {
        await returnOrder({
          orderId,
          data: {
            reason: fulfillForm.remarks || "Order Return Processed",
            adminRemarks: fulfillForm.remarks || "",
            items: selectedReturnItems,
          },
        }).unwrap();

        showNotification("Return process initiated successfully.", "success");

        await triggerReviewsPending(orderId);
        onRefreshDashboard();
      } catch (err) {
        showNotification("Failed to process return request.", "error");
      }

      return;
    }

    //--------------------------------------------------
    // Standard / Slider fulfillment
    //--------------------------------------------------

    try {
      // Slider starts with Confirmed status.
      // After this, Slider controls the status through webhooks.
      const updatedStatus = isSlider ? ("5" as any) : fulfillForm.status;

      const payloadData = {
        ...fulfillForm,
        status: updatedStatus,
        statusDate: new Date().toISOString(),
      };

      await fulfillOrderUpdate({
        orderId,
        data: payloadData,
      }).unwrap();

      showNotification(
        isSlider
          ? "Slider delivery created successfully."
          : "Fulfillment updated successfully.",
        "success",
      );

      await triggerReviewsPending(orderId);
      onRefreshDashboard();
    } catch (err) {
      showNotification(
        isSlider
          ? "Could not create Slider delivery."
          : "Could not update fulfillment.",
        "error",
      );
    }
  };

  const openGoogleMaps = () => {
    if (!reviewDetails?.address) return;
    const { addressLine1, addressLine2, city, state, country } =
      reviewDetails.address;
    const fullAddress = [addressLine1, addressLine2, city, state, country]
      .filter(Boolean)
      .join(", ");
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        fullAddress,
      )}`,
      "_blank",
    );
  };

  const getActivityTypeBadge = (type: string) => {
    switch (type) {
      case "Status":
        return {
          icon: <Clock size={14} className="text-purple-600" />,
          bg: "bg-purple-50 text-purple-700 border-purple-200",
        };
      case "Payment":
        return {
          icon: <CreditCard size={14} className="text-emerald-600" />,
          bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
        };
      case "Refund":
        return {
          icon: <Wallet size={14} className="text-rose-600" />,
          bg: "bg-rose-50 text-rose-700 border-rose-200",
        };
      case "Shipment":
        return {
          icon: <Truck size={14} className="text-blue-600" />,
          bg: "bg-blue-50 text-blue-700 border-blue-200",
        };
      case "Revision":
        return {
          icon: <Repeat size={14} className="text-amber-600" />,
          bg: "bg-amber-50 text-amber-700 border-amber-200",
        };
      case "Review":
        return {
          icon: <ShieldCheck size={14} className="text-indigo-600" />,
          bg: "bg-indigo-50 text-indigo-700 border-indigo-200",
        };
      default:
        return {
          icon: <Info size={14} className="text-slate-600" />,
          bg: "bg-slate-100 text-slate-700 border-slate-200",
        };
    }
  };

  const p = reviewDetails?.pricing;

  // Product-level savings are calculated from the API's original unit price
  // (unitPrice) versus the actual sale unit price (unitSalePrice).
  // This is display-only and is intentionally kept separate from coupon,
  // rewards, wallet, VAT, and other order-level pricing adjustments.
  const productSavings = useMemo(() => {
    return (reviewDetails?.items || []).reduce((total: number, item: any) => {
      const originalUnitPrice = Number(
        item.unitPrice ?? item.unitSalePrice ?? 0,
      );
      const saleUnitPrice = Number(item.unitSalePrice ?? 0);
      const quantity = Number(item.quantity ?? 0);

      return total + Math.max(0, originalUnitPrice - saleUnitPrice) * quantity;
    }, 0);
  }, [reviewDetails?.items]);

  const rawActivities =
    activityData?.orders?.find((o) => o.orderId === orderId)?.activities ||
    (Array.isArray(activityData) ? (activityData as any) : []);

  // Move useMemo UP above the loading check
  const activitiesList = useMemo(() => {
    if (!rawActivities || !Array.isArray(rawActivities)) return [];
    return [...rawActivities].sort((a, b) => {
      const dateA = new Date(a.createdOn).getTime();
      const dateB = new Date(b.createdOn).getTime();
      return dateB - dateA;
    });
  }, [rawActivities]);

  const reviewHistoryList = reviewDetails?.reviewHistory || [];
  const returnsList = (reviewDetails as any)?.returns || [];
  const latestReturn = returnsList[0] || null;
  if (isReviewDetailsLoading && !reviewDetails) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-5xl bg-[#F4F4F2] flex h-full items-center justify-center">
          <RefreshCw className="animate-spin text-[#6DBE45]" size={32} />
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm transition-opacity duration-300 order-detail-drawer-responsive">
      <div className="flex-1" onClick={onClose} />
      <div className="w-full max-w-5xl bg-[#F4F4F2] flex flex-col md:flex-row h-full overflow-hidden shadow-2xl relative animate-in slide-in-from-right duration-200 order-detail-drawer-panel">
        <style>{`
          /* ============================================================
             MOBILE ONLY — desktop styles above/below remain unchanged
             Breakpoint: phones/tablets below Tailwind md (768px)
             ============================================================ */
          @media (max-width: 767px) {
            .order-detail-drawer-responsive {
              width: 100%;
              height: 100dvh;
              min-height: 100dvh;
            }

            .order-detail-drawer-panel {
              width: 100% !important;
              max-width: 100% !important;
              height: 100dvh !important;
              min-height: 100dvh !important;
              max-height: 100dvh !important;
              overflow: hidden !important;
            }

            /* Keep the summary usable without taking the entire phone screen. */
            .order-detail-drawer-summary {
              width: 100% !important;
              max-width: 100% !important;
              max-height: 38dvh;
              overflow-x: hidden;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
              flex: 0 1 auto;
            }

            .order-detail-drawer-summary > div:first-child {
              position: sticky;
              top: 0;
              z-index: 20;
              padding: 14px 16px;
            }

            .order-detail-drawer-summary > div:not(:first-child) {
              padding-left: 16px;
              padding-right: 16px;
              padding-top: 16px;
              padding-bottom: 16px;
            }

            .order-detail-drawer-workspace {
              width: 100%;
              min-width: 0;
              min-height: 0;
              flex: 1 1 0%;
              overflow: hidden;
            }

            /* Compact mobile workspace header; desktop header is untouched. */
            .order-detail-drawer-workspace > div:first-child {
              padding: 12px 16px;
              gap: 10px;
              min-height: 64px;
            }

            .order-detail-drawer-workspace > div:first-child > div:first-child {
              min-width: 0;
              flex: 1 1 auto;
            }

            .order-detail-drawer-workspace > div:first-child h2 {
              font-size: 16px;
              line-height: 1.25;
            }

            .order-detail-drawer-workspace > div:first-child p {
              margin-top: 3px;
              line-height: 1.35;
              max-width: 100%;
            }

            .order-detail-drawer-workspace > div:nth-child(2) {
              min-width: 0;
              min-height: 0;
              overflow-x: hidden;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
            }

            /* Reduce content padding only on phones. */
            .order-detail-drawer-workspace > div:nth-child(2) > div {
              padding: 16px !important;
            }

            /* Tabs stay usable on narrow screens instead of squeezing text. */
            .order-detail-drawer-workspace .sticky.top-0 {
              max-width: 100%;
              overflow-x: auto;
              flex-wrap: nowrap;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
            }

            .order-detail-drawer-workspace .sticky.top-0::-webkit-scrollbar {
              display: none;
            }

            .order-detail-drawer-workspace .sticky.top-0 > button {
              flex: 0 0 auto;
              white-space: nowrap;
            }

            /* Prevent iOS Safari from zooming when focusing form controls. */
            .order-detail-drawer-responsive input,
            .order-detail-drawer-responsive select,
            .order-detail-drawer-responsive textarea {
              font-size: 16px !important;
            }

            /* Tables/cards with wide content can scroll horizontally on phones. */
            .order-detail-drawer-responsive .overflow-x-auto {
              max-width: 100%;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }

            /* Keep long text from forcing the drawer wider than the viewport. */
            .order-detail-drawer-responsive {
              overflow-x: hidden;
            }

            .order-detail-drawer-responsive button,
            .order-detail-drawer-responsive a {
              max-width: 100%;
            }

            /* Mobile notification should not cover the close/action area. */
            .order-detail-drawer-panel > .absolute.top-4.right-4 {
              top: 8px !important;
              right: 8px !important;
              left: 8px !important;
              width: auto;
              max-width: calc(100% - 16px);
            }
          }
        `}</style>
        {notification && (
          <div
            className={`absolute top-4 right-4 z-[100] px-4 py-3 rounded-xl shadow-lg border text-xs font-bold flex items-center gap-2 ${notification.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
              }`}
          >
            <AlertCircle size={14} />
            {notification.message}
          </div>
        )}

        {/* Left Side Summary Panel */}
        <div className="w-full md:w-80 bg-white flex flex-col border-b md:border-b-0 md:border-r border-slate-200/80 shrink-0 md:overflow-y-auto order-detail-drawer-summary">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <FileText size={14} style={{ color: "#8B6B3E" }} /> Order Summary
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-slate-400 hover:bg-slate-50 md:hidden"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Order Number
              </p>
              <p
                className="text-xl font-black mt-0.5"
                style={{ color: "#2F5E2E" }}
              >
                {reviewDetails?.header?.orderNumber || "..."}
              </p>
            </div>

            <div className="flex items-center gap-2 text-slate-600 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
              <Calendar size={14} style={{ color: "#8B6B3E" }} />
              <span className="text-xs font-semibold">
                {reviewDetails?.header?.orderDate
                  ? new Date(
                    reviewDetails.header.orderDate,
                  ).toLocaleDateString()
                  : "..."}
              </span>
              <span className="text-xs font-semibold">
                {reviewDetails?.header?.orderDate
                  ? new Date(
                    reviewDetails.header.orderDate,
                  ).toLocaleTimeString()
                  : "..."}
              </span>
            </div>

            <div className="space-y-1.5 border-t border-slate-100 pt-5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Order Status
              </p>
              <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold border border-blue-200 bg-blue-50 text-blue-700">
                  {reviewDetails?.header?.orderStatus || "..."}
                </span>

                {/* Return Status Badge */}
                {latestReturn && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border border-amber-300 bg-amber-50 text-amber-800">
                    <RotateCcw size={12} /> Return: {latestReturn.status}
                  </span>
                )}

                {reviewDetails?.header?.paymentStatus && (
                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold border border-purple-200 bg-purple-50 text-purple-700">
                    {reviewDetails.header.paymentStatus}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Customer Details
              </p>
              <div className="flex items-start gap-2.5">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-500 mt-0.5">
                  <User size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-800 truncate">
                    {reviewDetails?.customer?.name || "..."}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {reviewDetails?.customer?.email || "..."}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {reviewDetails?.customer?.mobile || ""}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Shipping Address
                </p>
                {reviewDetails?.address && (
                  <button
                    onClick={openGoogleMaps}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-md flex items-center gap-1 text-[11px] font-bold transition-colors"
                  >
                    <MapPin size={13} /> Open Map
                  </button>
                )}
              </div>
              <div className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed">
                <p className="font-bold text-slate-800">
                  {reviewDetails?.address?.fullName}
                </p>
                <p>{reviewDetails?.address?.addressLine1}</p>
                {reviewDetails?.address?.addressLine2 && (
                  <p>{reviewDetails.address.addressLine2}</p>
                )}
                <p>
                  {reviewDetails?.address?.city},{" "}
                  {reviewDetails?.address?.state}
                </p>
                <p className="font-semibold text-slate-500 mt-1">
                  {reviewDetails?.address?.country} -{" "}
                  {reviewDetails?.address?.postalCode}
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Summary Section */}
          {/* Pricing Summary Section */}
          <div className="border-t border-slate-100 px-6 py-5 space-y-4 bg-slate-50/70 backdrop-blur-md mt-auto">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Receipt size={12} /> Pricing Details
            </p>

            {/* --------------------------------------------- */}
            {/* ORDER PRICING */}
            {/* --------------------------------------------- */}

            <div className="space-y-2 text-xs font-medium text-slate-600">
              {/* Subtotal */}
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="text-slate-900 font-semibold">
                  AED {(p?.subTotal ?? p?.productTotal ?? 0).toFixed(2)}
                </span>
              </div>

              {/* Product Savings */}
              {productSavings > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Product Savings</span>
                  <span className="font-semibold">
                    - AED {productSavings.toFixed(2)}
                  </span>
                </div>
              )}
              {Number(p?.remainingAmount ?? 0) > 0 && (
                <div className="mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50/60 space-y-3">

                  <div className="flex items-center gap-2">
                    <CreditCard
                      size={14}
                      className="text-amber-700"
                    />

                    <p className="text-xs font-bold text-amber-800">
                      Remaining Payment
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-600">
                      Amount Due
                    </span>

                    <span className="text-sm font-black text-amber-700">
                      AED {Number(p?.remainingAmount).toFixed(2)}
                    </span>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                      Payment Reference / Note
                    </label>

                    <input
                      type="text"
                      value={fullPaymentReference}
                      onChange={(e) =>
                        setFullPaymentReference(e.target.value)
                      }
                      placeholder="Enter payment reference"
                      className="w-full px-3 py-2.5 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:border-[#6DBE45] focus:ring-1 focus:ring-[#6DBE45]"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleMarkFullPayment}

                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#2F5E2E] text-white text-xs font-bold hover:bg-[#244b24] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isMarkingFullPayment ? (
                      <>
                        <RefreshCw
                          size={14}
                          className="animate-spin"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={14} />
                        Mark Full Payment
                      </>
                    )}
                  </button>

                </div>
              )}

              {/* Coupon */}
              {!!p?.couponDiscount && p.couponDiscount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span className="flex items-center gap-1">
                    <Percent size={11} />
                    Coupon Discount
                  </span>

                  <span className="font-semibold">
                    - AED {p.couponDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Reward Points */}
              {!!p?.rewardPointDiscount && p.rewardPointDiscount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span className="flex items-center gap-1">
                    <Gift size={12} />
                    Petland Points ({p.rewardPointsUsed ?? 0})
                  </span>

                  <span className="font-semibold">
                    - AED {p.rewardPointDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              {/* VAT */}
              <div className="flex justify-between text-slate-500">
                <span>VAT</span>

                <span className="font-semibold text-slate-700">
                  AED {(p?.vatAmount ?? 0).toFixed(2)}
                </span>
              </div>

              {/* Shipping */}
              <div className="flex justify-between">
                <span>Shipping Fee</span>

                <span className="text-slate-900 font-semibold">
                  {p?.shippingAmount && p.shippingAmount > 0
                    ? `AED ${p.shippingAmount.toFixed(2)}`
                    : "FREE"}
                </span>
              </div>

              {/* Platform Fee */}
              {!!p?.platformFee && p.platformFee > 0 && (
                <div className="flex justify-between">
                  <span>Platform Fee</span>

                  <span className="text-slate-900 font-semibold">
                    AED {p.platformFee.toFixed(2)}
                  </span>
                </div>
              )}

              {/* --------------------------------------------- */}
              {/* ORDER TOTAL */}
              {/* --------------------------------------------- */}

              <div className="flex justify-between font-bold text-slate-900 pt-2 mt-2 border-t border-slate-200/70">
                <span>Order Total</span>

                <span>AED {(p?.totalAmount ?? p?.total ?? 0).toFixed(2)}</span>
              </div>
            </div>

            {/* --------------------------------------------- */}
            {/* PAYMENT & ADJUSTMENTS */}
            {/* --------------------------------------------- */}

            {((p?.walletAmountUsed ?? 0) > 0 ||
              (p?.additionalPaymentAmount ?? 0) > 0 ||
              (p?.refundedAmount ?? 0) > 0) && (
                <div className="pt-3 border-t border-slate-200/60 space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Payment & Adjustments
                  </p>

                  {/* Wallet */}
                  {!!p?.walletAmountUsed && p.walletAmountUsed > 0 && (
                    <div className="flex justify-between items-center bg-red-50 px-2.5 py-2 rounded-lg border border-red-100">
                      <span className="flex items-center gap-1.5 text-red-800 font-semibold">
                        <Wallet size={12} className="text-red-600" />
                        Wallet Used
                      </span>

                      <span className="text-red-700 font-bold">
                        - AED {p.walletAmountUsed.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {/* Additional Payment */}
                  {!!p?.additionalPaymentAmount &&
                    p.additionalPaymentAmount > 0 && (
                      <div className="flex justify-between items-center bg-blue-50 px-2.5 py-2 rounded-lg border border-blue-200">
                        <span className="flex items-center gap-1.5 text-blue-900 font-semibold">
                          <CreditCard size={12} className="text-blue-600" />
                          Additional Payment
                        </span>

                        <span className="text-blue-700 font-extrabold">
                          + AED {p.additionalPaymentAmount.toFixed(2)}
                        </span>
                      </div>
                    )}

                  {/* Refunded Amount */}
                  {!!p?.refundedAmount && p.refundedAmount > 0 && (
                    <div className="flex justify-between items-center bg-red-50 px-2.5 py-2 rounded-lg border border-red-200">
                      <span className="flex items-center gap-1.5 text-red-900 font-semibold">
                        <RotateCcw size={12} className="text-red-600" />
                        Refunded Amount
                      </span>

                      <span className="text-red-700 font-extrabold">
                        - AED {p.refundedAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

            {/* --------------------------------------------- */}
            {/* NET AMOUNT */}
            {/* --------------------------------------------- */}

            {((p?.additionalPaymentAmount ?? 0) > 0 ||
              (p?.refundedAmount ?? 0) > 0) && (
                <div className="pt-2 border-t border-slate-200/60">
                  <div className="flex justify-between items-center bg-slate-100 px-3 py-2.5 rounded-lg border border-slate-200 font-extrabold text-xs">
                    <span className="flex items-center gap-1.5 text-slate-800">
                      <Receipt size={13} className="text-slate-600" />
                      Net Amount
                    </span>

                    <span className="text-slate-900">
                      AED{" "}
                      {(
                        (p?.totalAmount ?? p?.total ?? 0) +
                        (p?.additionalPaymentAmount ?? 0) -
                        (p?.refundedAmount ?? 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Right Side Workspace Framework */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 order-detail-drawer-workspace">
          <div className="px-6 md:px-8 py-5 border-b border-slate-200/60 bg-white flex items-center justify-between shadow-sm shrink-0">
            <div>
              <h2 className="text-lg font-black text-slate-900">
                Order Details
              </h2>
              <p className="text-xs text-slate-400">
                Review items, history activity logs, and manage status decisions
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="hidden md:flex p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 shadow-sm transition-colors"
              >
                <X size={16} />
              </button>
              <button
                onClick={handleDownloadInvoice}
                disabled={isCompilingInvoice}
                className="p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 shadow-sm transition-colors"
              >
                {isCompilingInvoice ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Download size={16} style={{ color: "#5FB63F" }} />
                )}
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            <div
              className={`p-6 md:p-8 space-y-6 ${showSaveBar ? "pb-32" : "pb-8"
                }`}
            >
              <div className="space-y-4">
                {/* 3-Tab Navigation Bar */}
                <div className="flex flex-wrap gap-2 p-1.5 bg-white rounded-xl border border-slate-200/60 shadow-sm max-w-xl sticky top-0 z-10">
                  <button
                    onClick={() => setActiveTab("items")}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${activeTab === "items"
                      ? "bg-[#6DBE45] text-white shadow-sm"
                      : "text-slate-500 hover:bg-slate-50"
                      }`}
                  >
                    Products ({revisionItems.length})
                  </button>
                  <button
                    onClick={() => setActiveTab("activity")}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${activeTab === "activity"
                      ? "bg-[#6DBE45] text-white shadow-sm"
                      : "text-slate-500 hover:bg-slate-50"
                      }`}
                  >
                    History
                  </button>
                  <button
                    onClick={() => setActiveTab("refunds")}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${activeTab === "refunds"
                      ? "bg-[#6DBE45] text-white shadow-sm"
                      : "text-slate-500 hover:bg-slate-50"
                      }`}
                  >
                    Refund
                  </button>
                </div>

                {/* Return Request Active Banner */}
                {activeTab === "items" && latestReturn && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 flex items-center justify-between gap-4 animate-in fade-in">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-amber-100 text-amber-800 shrink-0">
                        <RotateCcw size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-amber-900">
                          Return Order Initiated ({latestReturn.status})
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-amber-200 text-amber-900 border border-amber-300">
                      {latestReturn.status}
                    </span>
                  </div>
                )}

                {/* 1. WAITING FOR CUSTOMER APPROVAL BANNER */}
                {activeTab === "items" &&
                  hasPendingRevision &&
                  !isPendingSettlement && (
                    <div
                      className={`rounded-2xl border p-4 flex items-start justify-between gap-4 ${isExpired
                        ? "bg-slate-50 border-slate-200"
                        : "bg-amber-50 border-amber-200"
                        }`}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div
                          className={`p-2 rounded-xl ${isExpired
                            ? "bg-slate-100 text-slate-400"
                            : "bg-amber-100 text-amber-700"
                            }`}
                        >
                          <Clock size={16} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-bold ${isExpired ? "text-slate-600" : "text-amber-800"
                              }`}
                          >
                            {isExpired
                              ? "Revision request expired"
                              : "Waiting for customer approval"}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {isExpired
                              ? "The revision time limit has passed. You can now revise the items again."
                              : "A revision has already been sent for this order. No further action is available here until the customer responds."}
                          </p>
                        </div>
                      </div>

                      {!isExpired && expiresOnMs && (
                        <div className="flex flex-col items-end shrink-0">
                          <div className="flex items-center gap-1 text-slate-600">
                            <Clock size={16} />
                            <span className="text-xs font-medium">
                              Expires in
                            </span>
                          </div>
                          <p className="text-2xl font-extrabold text-red-600 leading-none mt-1">
                            {formatCountdown(remainingMs || 0)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                {/* 2. PENDING SETTLEMENT PANEL */}
                {activeTab === "items" && isPendingSettlement && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-4">
                    {isPaymentPending && (
                      <div className="flex items-start gap-3">
                        <Clock size={18} className="text-emerald-700 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-emerald-900">
                            Revision accepted — awaiting customer payment
                            settlement
                          </p>
                          <p className="text-xs text-emerald-700/80 mt-1">
                            The customer accepted this revision. Remaining
                            difference of AED{" "}
                            {currentRevision?.differenceAmount?.toFixed(2)} is
                            pending payment.
                          </p>
                        </div>
                      </div>
                    )}

                    {isRefundPending && (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800 shrink-0 mt-0.5">
                            <Clock size={18} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-emerald-900">
                              Revision Accepted — Refund Required
                            </p>
                            <p className="text-xs text-emerald-700 mt-0.5">
                              The customer accepted this revision. Adjust amount
                              if required, select settlement method, and
                              confirm.
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-3.5 rounded-xl border border-emerald-200/80 gap-3 shadow-xs">
                          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                            Settlement Amount (AED)
                          </span>
                          <div className="relative w-full sm:w-44">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                              AED
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={editableSettlementAmount}
                              onChange={(e) =>
                                setEditableSettlementAmount(e.target.value)
                              }
                              className="w-full pl-11 pr-3 py-2 border border-emerald-300 rounded-lg text-sm bg-white font-mono font-bold text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all text-right"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setSelectedRefundMethod("Wallet")}
                            className={`p-3.5 rounded-xl border text-left flex items-start gap-3 transition-all ${selectedRefundMethod === "Wallet"
                              ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-sm"
                              : "bg-white/70 border-slate-200 hover:border-slate-300"
                              }`}
                          >
                            <div
                              className={`rounded-lg shrink-0 ${selectedRefundMethod === "Wallet"
                                ? "bg-emerald-50 text-[#2F5E2E]"
                                : "bg-slate-100 text-slate-400"
                                }`}
                            >
                              <Wallet size={18} />
                            </div>
                            <p
                              className={`text-xs font-bold ${selectedRefundMethod === "Wallet"
                                ? "text-slate-900"
                                : "text-slate-700"
                                }`}
                            >
                              Refund to Customer Wallet
                            </p>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setSelectedRefundMethod("OriginalPayment")
                            }
                            className={`p-3.5 rounded-xl border text-left flex items-start gap-3 transition-all ${selectedRefundMethod === "OriginalPayment"
                              ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-sm"
                              : "bg-white/70 border-slate-200 hover:border-slate-300"
                              }`}
                          >
                            <div
                              className={`rounded-lg shrink-0 ${selectedRefundMethod === "OriginalPayment"
                                ? "bg-emerald-50 text-[#2F5E2E]"
                                : "bg-slate-100 text-slate-400"
                                }`}
                            >
                              <CreditCard size={18} />
                            </div>
                            <p
                              className={`text-xs font-bold ${selectedRefundMethod === "OriginalPayment"
                                ? "text-slate-900"
                                : "text-slate-700"
                                }`}
                            >
                              Original Payment Source
                            </p>
                          </button>
                        </div>

                        <button
                          type="button"
                          disabled={
                            isRefundSubmitting || !editableSettlementAmount
                          }
                          onClick={() =>
                            handleProcessSettlementRefund(
                              "Revision settlement refund",
                            )
                          }
                          className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-[#6DBE45] text-white font-bold text-xs hover:bg-[#5FB63F] shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                        >
                          {isRefundSubmitting ? (
                            <>
                              <RefreshCw size={14} className="animate-spin" />
                              Processing Refund...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 size={14} />
                              Confirm & Process Refund (AED{" "}
                              {Number(editableSettlementAmount || 0).toFixed(2)}
                              )
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* 3. CANCELLED ORDER REFUND PANEL */}
                {activeTab === "items" && isCancelled && !isAlreadyRefunded && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800 shrink-0 mt-0.5">
                        <XCircle size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-emerald-900">
                          Order Cancelled — Process Refund
                        </p>
                        <p className="text-xs text-emerald-700 mt-0.5">
                          This order was cancelled. Adjust the refund amount if
                          necessary, choose settlement method, and process.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-3.5 rounded-xl border border-emerald-200 gap-3 shadow-xs">
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                        Refund Amount (AED)
                      </span>
                      <div className="relative w-full sm:w-44">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                          AED
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={editableSettlementAmount}
                          onChange={(e) =>
                            setEditableSettlementAmount(e.target.value)
                          }
                          className="w-full pl-11 pr-3 py-2 border border-emerald-300 rounded-lg text-sm bg-white font-mono font-bold text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all text-right"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedRefundMethod("Wallet")}
                        className={`p-3.5 rounded-xl border text-left flex items-start gap-3 transition-all ${selectedRefundMethod === "Wallet"
                          ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-sm"
                          : "bg-white/70 border-slate-200 hover:border-slate-300"
                          }`}
                      >
                        <div
                          className={`rounded-lg shrink-0 ${selectedRefundMethod === "Wallet"
                            ? "bg-emerald-50 text-[#2F5E2E]"
                            : "bg-slate-100 text-slate-400"
                            }`}
                        >
                          <Wallet size={18} />
                        </div>
                        <p
                          className={`text-xs font-bold ${selectedRefundMethod === "Wallet"
                            ? "text-slate-900"
                            : "text-slate-700"
                            }`}
                        >
                          Refund to Customer Wallet
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setSelectedRefundMethod("OriginalPayment")
                        }
                        className={`p-3.5 rounded-xl border text-left flex items-start gap-3 transition-all ${selectedRefundMethod === "OriginalPayment"
                          ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-sm"
                          : "bg-white/70 border-slate-200 hover:border-slate-300"
                          }`}
                      >
                        <div
                          className={`rounded-lg shrink-0 ${selectedRefundMethod === "OriginalPayment"
                            ? "bg-emerald-50 text-[#2F5E2E]"
                            : "bg-slate-100 text-slate-400"
                            }`}
                        >
                          <CreditCard size={18} />
                        </div>
                        <p
                          className={`text-xs font-bold ${selectedRefundMethod === "OriginalPayment"
                            ? "text-slate-900"
                            : "text-slate-700"
                            }`}
                        >
                          Original Payment Source
                        </p>
                      </button>
                    </div>

                    <button
                      type="button"
                      disabled={isRefundSubmitting || !editableSettlementAmount}
                      onClick={() =>
                        handleProcessSettlementRefund("Cancelled Order Refund")
                      }
                      className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-[#6DBE45] text-white font-bold text-xs hover:bg-[#5FB63F] shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                    >
                      {isRefundSubmitting ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Processing Refund...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={14} />
                          Process Cancelled Order Refund (AED{" "}
                          {Number(editableSettlementAmount || 0).toFixed(2)})
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* 4. READ-ONLY STATUS BANNER */}
                {activeTab === "items" &&
                  !hasPendingRevision &&
                  !isPendingSettlement &&
                  !showFulfillmentPanel &&
                  !isUnderReview && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-3">
                      <CheckCircle2 size={16} className="text-slate-400" />
                      <p className="text-sm font-semibold text-slate-500">
                        {isCancelled
                          ? isAlreadyRefunded
                            ? `This order was cancelled and payment status is ${paymentStatus}.`
                            : "This order has been cancelled."
                          : "This order revision has already been decided."}
                      </p>
                    </div>
                  )}

                {/* 5. FULFILLMENT PANEL */}
                {showFulfillmentPanel && (
                  <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-b from-white to-slate-50/50 p-6 space-y-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-2xs">
                          <Truck size={18} />
                        </div>
                        <div>
                          <h3 className="text-sm font-black text-slate-900">
                            Fulfillment
                          </h3>
                          <p className="text-xs text-slate-400">
                            Order confirmed — manage carrier & shipment status
                          </p>
                        </div>
                      </div>

                      {selectedProvider && !isProviderLocked && (
                        <button
                          type="button"
                          onClick={() => setSelectedProvider(null)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:border-emerald-500 hover:text-emerald-600 transition-all shadow-2xs"
                        >
                          <Settings2 size={12} />
                          Change Provider
                        </button>
                      )}
                    </div>

                    {!selectedProvider && !isProviderLocked ? (
                      <div className="space-y-3">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          Choose Delivery Carrier
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedProvider("Manual");
                              setFulfillForm((prev) => ({
                                ...prev,
                                provider: "Manual",
                              }));
                            }}
                            className="p-4 rounded-xl border border-slate-200 bg-white hover:border-[#6DBE45] hover:ring-2 hover:ring-[#6DBE45]/15 text-left flex items-start gap-3 transition-all shadow-2xs group"
                          >
                            <div className="p-2.5 rounded-lg bg-slate-100 group-hover:bg-emerald-50 group-hover:text-emerald-700 text-slate-500 transition-colors">
                              <Truck size={20} />
                            </div>
                            <div>
                              <p className="text-xs font-black text-slate-800 group-hover:text-emerald-800">
                                Manual Shipping
                              </p>
                              <p className="text-[11px] text-slate-400 mt-0.5">
                                Manage status, courier name & tracking manually
                              </p>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedProvider("Slider");
                              setFulfillForm((prev) => ({
                                ...prev,
                                provider: "Slider",
                              }));
                            }}
                            className="p-4 rounded-xl border border-slate-200 bg-white hover:border-[#6DBE45] hover:ring-2 hover:ring-[#6DBE45]/15 text-left flex items-start gap-3 transition-all shadow-2xs group"
                          >
                            <div className="p-2.5 rounded-lg bg-slate-100 group-hover:bg-emerald-50 group-hover:text-emerald-700 text-slate-500 transition-colors">
                              <RefreshCw size={20} />
                            </div>
                            <div>
                              <p className="text-xs font-black text-slate-800 group-hover:text-emerald-800">
                                Slider Courier
                              </p>
                              <p className="text-[11px] text-slate-400 mt-0.5">
                                Automated fulfillment & live tracking
                                integration
                              </p>
                            </div>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 animate-in fade-in duration-200">
                        <div className="flex items-center justify-between bg-emerald-50/60 rounded-xl border border-emerald-100 px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-xs font-bold text-emerald-900">
                              Active Carrier:{" "}
                              <span className="uppercase text-emerald-700 font-extrabold">
                                {selectedProvider}
                              </span>
                            </span>
                          </div>
                          {isProviderLocked && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-white px-2 py-0.5 rounded border border-slate-200">
                              Locked
                            </span>
                          )}
                        </div>

                        {selectedProvider === "Slider" ? (
                          <div className="space-y-4">
                            {activeSelectedProviderObj?.selected &&
                              activeSelectedProviderObj?.provider === "Slider" ? (
                              <div className="p-4 bg-emerald-50/80 border border-emerald-200 rounded-2xl flex items-center gap-3">
                                <CheckCircle2
                                  size={18}
                                  className="text-emerald-600 shrink-0"
                                />
                                <div>
                                  <p className="text-xs font-bold text-emerald-900">
                                    Slider Automated Dispatch Active
                                  </p>
                                  <p className="text-[11px] text-emerald-700 mt-0.5">
                                    Current Status:{" "}
                                    <strong className="uppercase">
                                      {reviewDetails?.header?.orderStatus ||
                                        "Processing"}
                                    </strong>{" "}
                                    (Updates handled via Slider API)
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-2">
                                    Select Vehicle Type
                                  </label>
                                  <div className="grid grid-cols-2 gap-3">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setFulfillForm((prev) => ({
                                          ...prev,
                                          vehicleType: "bike",
                                        }))
                                      }
                                      className={`p-3.5 rounded-xl border text-left flex items-center gap-3 transition-all ${fulfillForm.vehicleType === "bike"
                                        ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-xs"
                                        : "bg-white/60 border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                      <div
                                        className={`p-2 rounded-lg ${fulfillForm.vehicleType === "bike"
                                          ? "bg-emerald-50 text-emerald-700"
                                          : "bg-slate-100 text-slate-400"
                                          }`}
                                      >
                                        <Bike size={20} />
                                      </div>
                                      <div>
                                        <p className="text-xs font-bold text-slate-800">
                                          Bike
                                        </p>
                                        <p className="text-[10px] text-slate-400">
                                          Light packages
                                        </p>
                                      </div>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() =>
                                        setFulfillForm((prev) => ({
                                          ...prev,
                                          vehicleType: "car",
                                        }))
                                      }
                                      className={`p-3.5 rounded-xl border text-left flex items-center gap-3 transition-all ${fulfillForm.vehicleType === "car"
                                        ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-xs"
                                        : "bg-white/60 border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                      <div
                                        className={`p-2 rounded-lg ${fulfillForm.vehicleType === "car"
                                          ? "bg-emerald-50 text-emerald-700"
                                          : "bg-slate-100 text-slate-400"
                                          }`}
                                      >
                                        <Car size={20} />
                                      </div>
                                      <div>
                                        <p className="text-xs font-bold text-slate-800">
                                          Car
                                        </p>
                                        <p className="text-[10px] text-slate-400">
                                          Bulky & heavy items
                                        </p>
                                      </div>
                                    </button>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={handleSaveFulfillment}
                                  disabled={isFulfillSubmitting}
                                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-[#6DBE45] hover:bg-[#5FB63F] shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                  {isFulfillSubmitting ? (
                                    <RefreshCw
                                      size={14}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <Save size={14} />
                                  )}
                                  Confirm & Enable Slider Dispatch
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Order Status
                                </label>
                                <select
                                  value={fulfillForm.status || ""}
                                  disabled={
                                    isFulfillSubmitting || isReturnSubmitting
                                  }
                                  onChange={(e) => {
                                    const newStatus = e.target
                                      .value as IOrderFullFIllRequest["status"];
                                    setFulfillForm((prev) => ({
                                      ...prev,
                                      status: newStatus,
                                    }));
                                  }}
                                  className="w-full mt-1.5 p-2.5 border border-slate-200 rounded-xl text-xs bg-white font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all disabled:opacity-60"
                                >
                                  <option value="" disabled>
                                    -- Select Order Status --
                                  </option>
                                  <option value="Packed">Packed</option>
                                  <option value="Shipped">Shipped</option>
                                  <option value="Delivered">Delivered</option>
                                  <option value="Cancelled">Cancelled</option>
                                  <option value="Return">Return</option>
                                </select>
                              </div>

                              <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Vehicle Type
                                </label>
                                <select
                                  value={fulfillForm.vehicleType}
                                  onChange={(e) =>
                                    setFulfillForm((prev) => ({
                                      ...prev,
                                      vehicleType: e.target
                                        .value as IOrderFullFIllRequest["vehicleType"],
                                    }))
                                  }
                                  className="w-full mt-1.5 p-2.5 border border-slate-200 rounded-xl text-xs bg-white font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                >
                                  <option value="bike">Bike</option>
                                  <option value="car">Car</option>
                                </select>
                              </div>

                              {fulfillForm.status === "Shipped" && (
                                <>
                                  <div className="animate-in fade-in duration-150">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                      Courier Partner
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="e.g. Aramex / DHL"
                                      value={fulfillForm.courierName}
                                      onChange={(e) =>
                                        setFulfillForm((prev) => ({
                                          ...prev,
                                          courierName: e.target.value,
                                        }))
                                      }
                                      className="w-full mt-1.5 p-2.5 border border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                    />
                                  </div>

                                  <div className="animate-in fade-in duration-150">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                      Tracking Code
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="e.g. TRK987654321"
                                      value={fulfillForm.trackingNumber}
                                      onChange={(e) =>
                                        setFulfillForm((prev) => ({
                                          ...prev,
                                          trackingNumber: e.target.value,
                                        }))
                                      }
                                      className="w-full mt-1.5 p-2.5 border border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                    />
                                  </div>
                                </>
                              )}

                              {fulfillForm.status === "Return" && (
                                <div className="sm:col-span-2 animate-in fade-in duration-150">
                                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                    Admin Remarks for Return
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Enter overall return remarks..."
                                    value={fulfillForm.remarks || ""}
                                    onChange={(e) =>
                                      setFulfillForm((prev) => ({
                                        ...prev,
                                        remarks: e.target.value,
                                      }))
                                    }
                                    className="w-full mt-1.5 p-2.5 border border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                                  />
                                </div>
                              )}
                            </div>

                            {/* Universal Save Button */}
                            {fulfillForm.status && (
                              <button
                                type="button"
                                onClick={handleSaveFulfillment}
                                disabled={
                                  isFulfillSubmitting || isReturnSubmitting
                                }
                                className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-[#6DBE45] hover:bg-[#5FB63F] shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all animate-in fade-in"
                              >
                                {isFulfillSubmitting || isReturnSubmitting ? (
                                  <RefreshCw
                                    size={14}
                                    className="animate-spin"
                                  />
                                ) : (
                                  <Save size={14} />
                                )}
                                {fulfillForm.status === "Return"
                                  ? "Process Return Order"
                                  : "Save Fulfillment Status"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Items Mapping Section */}
                <div className="space-y-3">
                  {activeTab === "items" && (
                    <>
                      {revisionItems.map((item, itemIdx) => {
                        const apiItem = reviewDetails?.items?.find(
                          (i) => i.orderItemId === item.orderItemId,
                        );

                        const displayImg = apiItem?.imageUrl || null;

                        const showActionButtons =
                          (!hasPendingRevision || isExpired) &&
                          !isPendingSettlement &&
                          (apiItem?.canRemove || apiItem?.canReplace);

                        const revEntry: any = getRevisionEntryForItem(item);
                        const isReplaced =
                          revEntry &&
                          Array.isArray(revEntry.replacements) &&
                          revEntry.replacements.length > 0;
                        const remainingQty =
                          revEntry?.remainingQuantity ?? item.originalQty;
                        const isTotallyRemoved =
                          revEntry && remainingQty === 0 && !isReplaced;
                        const isModifiedLine =
                          !!revEntry &&
                          (isReplaced ||
                            remainingQty < item.originalQty ||
                            revEntry.removedQuantity > 0);

                        const currentReturnState = returnItemsSelection[
                          item.orderItemId
                        ] || {
                          selected: false,
                          quantity: item.originalQty,
                          reason: "",
                        };

                        return (
                          <div
                            key={item.orderItemId}
                            className={`bg-white rounded-2xl p-5 border shadow-sm space-y-4 transition-colors ${isTotallyRemoved
                              ? "border-red-200 bg-red-50/20"
                              : item.status === "Reject"
                                ? "border-red-200 ring-1 ring-red-100"
                                : "border-slate-200/60"
                              }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Return Checkbox when Status is 'Return' */}
                                {fulfillForm.status === "Return" && (
                                  <label className="flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-lg border border-amber-200 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={currentReturnState.selected}
                                      onChange={(e) =>
                                        setReturnItemsSelection((prev) => ({
                                          ...prev,
                                          [item.orderItemId]: {
                                            ...prev[item.orderItemId],
                                            selected: e.target.checked,
                                          },
                                        }))
                                      }
                                      className="w-4 h-4 text-[#6DBE45] rounded border-slate-300 focus:ring-[#6DBE45]"
                                    />
                                    <span className="text-xs font-bold text-amber-900">
                                      Select for Return
                                    </span>
                                  </label>
                                )}

                                {hasPendingRevision ? (
                                  (() => {
                                    const outcome = getRevisionOutcome(item);
                                    const toneClasses: Record<string, string> =
                                    {
                                      accept:
                                        "bg-emerald-50 text-emerald-700 border-emerald-200",
                                      reject:
                                        "bg-red-50 text-red-700 border-red-200",
                                      replace:
                                        "bg-blue-50 text-blue-700 border-blue-200",
                                      partial:
                                        "bg-amber-50 text-amber-700 border-amber-200",
                                    };
                                    const toneIcon: Record<
                                      string,
                                      React.ReactNode
                                    > = {
                                      accept: <CheckCircle2 size={13} />,
                                      reject: <XCircle size={13} />,
                                      replace: <Repeat size={13} />,
                                      partial: <XCircle size={13} />,
                                    };
                                    return (
                                      <span
                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border ${toneClasses[outcome.tone]
                                          }`}
                                      >
                                        {toneIcon[outcome.tone]}
                                        {outcome.label}
                                      </span>
                                    );
                                  })()
                                ) : canProceedToFulfillment ? (
                                  (() => {
                                    const outcome = getRevisionOutcome(item);
                                    const toneClasses: Record<string, string> =
                                    {
                                      accept:
                                        "bg-emerald-50 text-emerald-700 border-emerald-200",
                                      reject:
                                        "bg-red-50 text-red-700 border-red-200",
                                      replace:
                                        "bg-blue-50 text-blue-700 border-blue-200",
                                      partial:
                                        "bg-amber-50 text-amber-700 border-amber-200",
                                    };
                                    const toneIcon: Record<
                                      string,
                                      React.ReactNode
                                    > = {
                                      accept: <CheckCircle2 size={13} />,
                                      reject: <XCircle size={13} />,
                                      replace: <Repeat size={13} />,
                                      partial: <XCircle size={13} />,
                                    };
                                    return (
                                      <span
                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border ${toneClasses[outcome.tone]
                                          }`}
                                      >
                                        {toneIcon[outcome.tone]}
                                        {outcome.label}
                                      </span>
                                    );
                                  })()
                                ) : (
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border ${item.status === "Accept"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-red-50 text-red-700 border-red-200"
                                      }`}
                                  >
                                    {item.status === "Accept" ? (
                                      <CheckCircle2 size={13} />
                                    ) : (
                                      <XCircle size={13} />
                                    )}
                                    {item.status}
                                  </span>
                                )}

                                {revEntry && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                                    Ordered:{" "}
                                    {revEntry.orderedQuantity ??
                                      item.originalQty}
                                    {revEntry.remainingQuantity !== undefined &&
                                      ` | Remaining: ${revEntry.remainingQuantity}`}
                                    {revEntry.replacementQuantity > 0 &&
                                      ` | Replaced: ${revEntry.replacementQuantity}`}
                                    {revEntry.removedQuantity > 0 &&
                                      ` | Removed: ${revEntry.removedQuantity}`}
                                  </span>
                                )}
                              </div>

                              {/* ITEM ACTION BUTTON */}
                              {/* ITEM ACTION BUTTON */}
                              {showActionButtons && (
                                <div className="ml-auto flex shrink-0 items-center gap-2">
                                  {(apiItem?.canRemove ||
                                    apiItem?.canReplace) && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleToggleReject(itemIdx)
                                        }
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${item.status === "Reject"
                                          ? "bg-red-500 text-white border-red-500 shadow-sm"
                                          : "bg-white text-slate-400 border-slate-200 hover:text-red-600 hover:border-red-300"
                                          }`}
                                      >
                                        <XCircle size={13} /> Reject
                                      </button>
                                    )}
                                </div>
                              )}
                            </div>

                            {/* Additional Return Item Specific Inputs */}
                            {fulfillForm.status === "Return" &&
                              currentReturnState.selected && (
                                <div className="p-3 bg-amber-50/50 border border-amber-200 rounded-xl space-y-2 animate-in fade-in">
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs font-bold text-slate-700">
                                      Return Qty:
                                    </span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={item.originalQty}
                                      value={currentReturnState.quantity}
                                      onChange={(e) =>
                                        setReturnItemsSelection((prev) => ({
                                          ...prev,
                                          [item.orderItemId]: {
                                            ...prev[item.orderItemId],
                                            quantity: Math.max(
                                              1,
                                              Math.min(
                                                Number(e.target.value) || 1,
                                                item.originalQty,
                                              ),
                                            ),
                                          },
                                        }))
                                      }
                                      className="w-16 p-1 border border-amber-300 rounded text-center text-xs font-bold bg-white"
                                    />
                                  </div>
                                  <input
                                    type="text"
                                    placeholder="Item return reason..."
                                    value={currentReturnState.reason}
                                    onChange={(e) =>
                                      setReturnItemsSelection((prev) => ({
                                        ...prev,
                                        [item.orderItemId]: {
                                          ...prev[item.orderItemId],
                                          reason: e.target.value,
                                        },
                                      }))
                                    }
                                    className="w-full p-2 border border-amber-300 rounded text-xs bg-white"
                                  />
                                </div>
                              )}

                            {/* REVISED ITEMS BREAKDOWN */}
                            {hasPendingRevision && isModifiedLine && (
                              <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-3.5 space-y-2.5">
                                <p className="text-[10px] font-bold uppercase tracking-wide text-blue-600 flex items-center gap-1">
                                  <Repeat size={11} /> Revised Items (Customer
                                  Receives)
                                </p>

                                {remainingQty > 0 && (
                                  <div className="flex items-center gap-3 bg-white rounded-lg border border-emerald-200 p-3 shadow-xs">
                                    {displayImg ? (
                                      <img
                                        src={displayImg}
                                        alt={item.productName}
                                        className="w-12 h-12 object-cover rounded-lg border border-slate-100 shrink-0"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-[10px] shrink-0 font-medium">
                                        No Img
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <p className="text-xs font-bold text-slate-800 truncate">
                                          {item.productName}
                                        </p>
                                        <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">
                                          Kept Original
                                        </span>
                                      </div>
                                      <p className="text-[10px] text-slate-500 mt-0.5">
                                        Qty: <strong>{remainingQty}</strong> •
                                        Unit Sale Price: AED{" "}
                                        {item.originalPrice.toFixed(2)}
                                      </p>
                                    </div>
                                    <div className="text-right whitespace-nowrap">
                                      <p className="text-xs font-black text-emerald-700">
                                        AED{" "}
                                        {(
                                          item.originalPrice * remainingQty
                                        ).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {isReplaced &&
                                  revEntry.replacements.map(
                                    (rep: any, ri: number) => (
                                      <div
                                        key={ri}
                                        className="flex items-center gap-3 bg-white rounded-lg border border-blue-200 p-3 shadow-xs"
                                      >
                                        {rep.imageUrl ? (
                                          <img
                                            src={rep.imageUrl}
                                            alt={rep.productName}
                                            className="w-12 h-12 object-cover rounded-lg border border-slate-100 shrink-0"
                                          />
                                        ) : (
                                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-[10px] shrink-0 font-medium">
                                            No Img
                                          </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <p className="text-xs font-bold text-slate-800 truncate">
                                              {rep.productName}
                                            </p>
                                            <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">
                                              Replacement
                                            </span>
                                          </div>
                                          <p className="text-[10px] text-slate-500 mt-0.5">
                                            Qty: <strong>{rep.quantity}</strong>{" "}
                                            • Variant ID: #
                                            {rep.productVariantId}
                                          </p>
                                        </div>
                                        <div className="text-right whitespace-nowrap">
                                          {rep.unitPrice >
                                            rep.unitSalePrice && (
                                              <p className="text-[10px] line-through text-slate-400">
                                                AED{" "}
                                                {Number(rep.unitPrice).toFixed(2)}
                                              </p>
                                            )}
                                          <p className="text-xs font-black text-blue-600">
                                            AED{" "}
                                            {(
                                              Number(rep.unitSalePrice) *
                                              Number(rep.quantity)
                                            ).toFixed(2)}
                                          </p>
                                        </div>
                                      </div>
                                    ),
                                  )}
                              </div>
                            )}

                            {/* ORIGINAL ITEM DISPLAY */}
                            <div
                              className={`flex items-start gap-3 pt-2 ${isModifiedLine
                                ? "border-t border-dashed border-red-200"
                                : ""
                                }`}
                            >
                              {displayImg ? (
                                <img
                                  src={displayImg}
                                  alt={item.productName}
                                  className={`w-14 h-14 object-cover rounded-lg border border-slate-100 shrink-0 ${isModifiedLine
                                    ? "opacity-50 grayscale-[40%]"
                                    : ""
                                    }`}
                                />
                              ) : (
                                <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-xs shrink-0 font-medium">
                                  No Img
                                </div>
                              )}

                              <div className="flex-1 min-w-0">
                                <h4
                                  className={`font-bold text-base ${isModifiedLine
                                    ? "line-through decoration-red-500 text-red-500/80"
                                    : "text-slate-900"
                                    }`}
                                >
                                  {item.productName}
                                </h4>

                                {apiItem?.subtitle && (
                                  <p
                                    className={`text-xs font-semibold ${isModifiedLine
                                      ? "line-through text-slate-400"
                                      : "text-slate-400"
                                      }`}
                                  >
                                    {apiItem.subtitle}
                                  </p>
                                )}

                                {item.isSubscription && (
                                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 w-fit">
                                    <Clock size={11} />
                                    <span>
                                      Subscription:{" "}
                                      <strong>
                                        {item.billingInterval} (
                                        {item.billingCycle})
                                      </strong>
                                    </span>
                                  </div>
                                )}

                                <div className="mt-1 space-y-1">
                                  <p className="text-xs font-semibold text-slate-500 flex items-center flex-wrap gap-1.5">
                                    {item.originalUnitPrice >
                                      item.originalPrice && (
                                        <span className="line-through text-slate-400">
                                          AED {item.originalUnitPrice.toFixed(2)}
                                        </span>
                                      )}

                                    <span
                                      className={
                                        item.originalUnitPrice >
                                          item.originalPrice
                                          ? "text-emerald-700 font-bold"
                                          : "text-slate-700 font-bold"
                                      }
                                    >
                                      AED {item.originalPrice.toFixed(2)}
                                    </span>

                                    <span>• Qty {item.originalQty}</span>

                                    <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 font-bold uppercase">
                                      {item.itemType}
                                    </span>
                                    {item.itemType === "Pet" && (
                                      <>
                                        {/* Pet Purchase Type */}
                                        {apiItem?.purchaseType && (
                                          <span className="text-[10px] bg-purple-50 px-1.5 py-0.5 rounded text-purple-700 font-bold uppercase border border-purple-100">
                                            {apiItem.purchaseType === "PartialBooking"
                                              ? "Partial Booking"
                                              : apiItem.purchaseType === "FullPayment"
                                                ? "Full Payment"
                                                : apiItem.purchaseType}
                                          </span>
                                        )}

                                        {/* Remaining Amount */}
                                        {apiItem?.remainingAmount !== undefined &&
                                          apiItem?.remainingAmount !== null && (
                                            <span className="text-[10px] bg-amber-50 px-1.5 py-0.5 rounded text-amber-700 font-bold border border-amber-100">
                                              Remaining: AED {Number(apiItem.remainingAmount).toFixed(2)}
                                            </span>
                                          )}
                                      </>
                                    )}
                                  </p>
                                  {item.itemType === "Service" && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Navigate or handle routing to the booking calendar/service screen
                                        // e.g., passing entityId or booking date as search/state params
                                        window.location.href = `/admin/adminbookingservice?serviceId=${apiItem?.entityId || ""}&date=${apiItem?.bookingDate || ""}`;
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 shadow-xs transition-all cursor-pointer"
                                    >
                                      <Calendar size={13} />
                                      View in Bookings
                                    </button>
                                  )}

                                  {item.youSaved > 0  && (
                                    <p className="text-[10px] font-bold text-emerald-600">
                                      You save AED{" "}
                                      {(
                                        (item.youSaved)
                                      ).toFixed(2)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {item.itemType === 'Pet' && item.purchaseAmount ? (<p
                                className={`text-sm font-black whitespace-nowrap ${isModifiedLine
                                  ? "text-red-400 line-through decoration-red-400"
                                  : "text-[#5FB63F]"
                                  }`}
                              >
                                AED{" "}
                                {(
                                  item?.purchaseAmount
                                ).toFixed(2)}
                              </p>) : (<p
                                className={`text-sm font-black whitespace-nowrap ${isModifiedLine
                                  ? "text-red-400 line-through decoration-red-400"
                                  : "text-[#5FB63F]"
                                  }`}
                              >
                                AED{" "}
                                {(
                                  item.totalPrice
                                )}
                              </p>)}

                            </div>

                            {revEntry?.reason && (
                              <p className="text-xs text-slate-500 italic bg-slate-50 p-2 rounded-lg border border-slate-100 flex items-center gap-1">
                                <Info
                                  size={12}
                                  className="text-slate-400 shrink-0"
                                />
                                Reason: "{revEntry.reason}"
                              </p>
                            )}

                            {item.status === "Reject" && showActionButtons && (
                              <div className="border border-red-200 bg-red-50/40 rounded-xl p-4 space-y-3 animate-in fade-in">
                                <div className="flex flex-wrap items-center gap-3">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-semibold text-slate-500">
                                      Reject Quantity:
                                    </span>
                                    <input
                                      type="number"
                                      min={1}
                                      max={item.originalQty}
                                      value={item.rejectQty}
                                      onChange={(e) =>
                                        updateItem(itemIdx, {
                                          rejectQty: Math.max(
                                            1,
                                            Math.min(
                                              Number(e.target.value) || 1,
                                              item.originalQty,
                                            ),
                                          ),
                                        })
                                      }
                                      className="w-16 p-1.5 border rounded-lg text-center font-bold bg-white text-xs"
                                    />
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {item.itemType === "Product" &&
                                      apiItem?.canReplace !== false && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleSetRejectMode(
                                              itemIdx,
                                              "Replace",
                                            )
                                          }
                                          className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${item.rejectMode === "Replace"
                                            ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                            : "bg-white text-slate-500 border-slate-200"
                                            }`}
                                        >
                                          Replace
                                        </button>
                                      )}
                                    {apiItem?.canRemove !== false && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleSetRejectMode(itemIdx, "Full")
                                        }
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${item.rejectMode === "Full"
                                          ? "bg-red-600 text-white border-red-600 shadow-sm"
                                          : "bg-white text-slate-500 border-slate-200"
                                          }`}
                                      >
                                        Reject
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {item.rejectMode === "Replace" &&
                                  item.itemType === "Product" && (
                                    <div className="bg-white p-4 rounded-xl text-xs space-y-3 border border-slate-200">
                                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <p className="font-bold text-slate-700">
                                          Suggested Replacement Products (Tap to
                                          Select Multiple):
                                        </p>
                                        <div className="relative w-full sm:w-60">
                                          <Search
                                            size={14}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                          />
                                          <input
                                            type="text"
                                            placeholder="Search (min 3 chars)..."
                                            value={item.replacementSearchTerm}
                                            onChange={(e) => {
                                              const term = e.target.value;

                                              updateItem(itemIdx, {
                                                replacementSearchTerm: term,
                                              });

                                              const trimmed = term.trim();
                                              if (
                                                trimmed.length >= 3 ||
                                                trimmed === ""
                                              ) {
                                                const timer = setTimeout(
                                                  async () => {
                                                    try {
                                                      const res =
                                                        await fetchReplacements(
                                                          {
                                                            orderId,
                                                            orderItemId:
                                                              item.orderItemId,
                                                            search: trimmed,
                                                          },
                                                        ).unwrap();
                                                      updateItem(itemIdx, {
                                                        replacementProducts:
                                                          res || [],
                                                      });
                                                    } catch (err) {
                                                      showNotification(
                                                        "Failed to search products.",
                                                        "error",
                                                      );
                                                    }
                                                  },
                                                  300,
                                                );

                                                return () =>
                                                  clearTimeout(timer);
                                              }
                                            }}
                                            className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-slate-50 focus:bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                                          />
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto p-1">
                                        {item.replacementProducts.map(
                                          (prod) => {
                                            const isSelected =
                                              item.selectedReplacements.some(
                                                (x) =>
                                                  x.product.productId ===
                                                  prod.productId,
                                              );
                                            const currentSelObj =
                                              item.selectedReplacements.find(
                                                (x) =>
                                                  x.product.productId ===
                                                  prod.productId,
                                              );

                                            return (
                                              <div
                                                key={prod.productId}
                                                onClick={() =>
                                                  toggleSelectReplacementProduct(
                                                    itemIdx,
                                                    prod,
                                                  )
                                                }
                                                className={`p-3 border rounded-xl flex gap-3 cursor-pointer transition-all ${isSelected
                                                  ? "border-blue-500 bg-blue-50/40 ring-1 ring-blue-400"
                                                  : "border-slate-200 bg-white hover:border-slate-300"
                                                  }`}
                                              >
                                                {prod.imageUrl ? (
                                                  <img
                                                    src={prod.imageUrl}
                                                    alt={prod.productName}
                                                    className="w-12 h-12 object-cover rounded border border-slate-100 shrink-0"
                                                  />
                                                ) : (
                                                  <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded flex items-center justify-center text-[10px] shrink-0 font-medium">
                                                    No Img
                                                  </div>
                                                )}
                                                <div
                                                  className="flex-1 min-w-0"
                                                  onClick={(e) =>
                                                    isSelected &&
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <p className="font-bold text-slate-900 truncate text-[13px]">
                                                    {prod.productName}
                                                  </p>
                                                  <p className="text-[10px] text-slate-500 truncate mt-0.5">
                                                    {prod.variantName ||
                                                      prod.brandName}
                                                  </p>
                                                  <p className="font-black text-blue-600 mt-1">
                                                    AED {prod.price.toFixed(2)}
                                                  </p>

                                                  {isSelected && (
                                                    <div className="mt-2 flex items-center gap-1.5">
                                                      <span className="text-[10px] text-slate-500">
                                                        Qty:
                                                      </span>
                                                      <input
                                                        type="number"
                                                        min={1}
                                                        value={
                                                          currentSelObj?.quantity ||
                                                          1
                                                        }
                                                        onChange={(e) =>
                                                          updateReplacementQuantity(
                                                            itemIdx,
                                                            prod.productId,
                                                            Number(
                                                              e.target.value,
                                                            ),
                                                          )
                                                        }
                                                        className="w-12 p-0.5 border text-center font-bold text-[11px] rounded bg-white"
                                                      />
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          },
                                        )}
                                        {item.replacementProducts.length ===
                                          0 && (
                                            <p className="text-slate-400 italic col-span-2 py-4 text-center">
                                              No structural replacement matches
                                              found.
                                            </p>
                                          )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Return History Accordion Section */}
                      {returnsList && returnsList.length > 0 && (
                        <div className="mt-6 border border-amber-200 rounded-2xl bg-amber-50/20 overflow-hidden shadow-xs">
                          <button
                            type="button"
                            onClick={() =>
                              setIsReturnHistoryOpen(!isReturnHistoryOpen)
                            }
                            className="w-full px-5 py-3.5 bg-amber-50 hover:bg-amber-100/80 flex items-center justify-between transition-colors border-b border-amber-200/60"
                          >
                            <div className="flex items-center gap-2">
                              <RotateCcw size={16} className="text-amber-800" />
                              <span className="text-xs font-black uppercase tracking-wider text-amber-900">
                                Return Orders History ({returnsList.length})
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-amber-800 font-bold">
                              <span>
                                {isReturnHistoryOpen ? "Hide" : "Show"} Details
                              </span>
                              {isReturnHistoryOpen ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </div>
                          </button>

                          {isReturnHistoryOpen && (
                            <div className="p-4 space-y-3 bg-white divide-y divide-slate-100">
                              {returnsList.map((ret: any) => (
                                <div
                                  key={ret.returnId}
                                  className="pt-3 first:pt-0 space-y-2 text-xs"
                                >
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-black text-slate-900">
                                        Return #{ret.returnId}
                                      </span>
                                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-amber-50 text-amber-800 border-amber-300">
                                        Status: {ret.status}
                                      </span>
                                    </div>
                                    <span className="text-[11px] font-semibold text-slate-400">
                                      {new Date(ret.createdOn).toLocaleString()}
                                    </span>
                                  </div>

                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200/80 font-medium text-slate-700 text-[11px]">
                                    <div>
                                      Items Count:{" "}
                                      <strong>{ret.itemCount || 0}</strong>
                                    </div>
                                    <div>
                                      Refund Amount:{" "}
                                      <strong className="text-emerald-700">
                                        AED{" "}
                                        {ret.refundAmount?.toFixed(2) || "0.00"}
                                      </strong>
                                    </div>
                                    <div>
                                      Created On:{" "}
                                      <strong>
                                        {new Date(
                                          ret.createdOn,
                                        ).toLocaleDateString()}
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Interactive Review History Accordion Section */}
                      {reviewHistoryList && reviewHistoryList.length > 0 && (
                        <div className="mt-6 border border-slate-200/80 rounded-2xl bg-white overflow-hidden shadow-xs">
                          <button
                            type="button"
                            onClick={() =>
                              setIsReviewHistoryOpen(!isReviewHistoryOpen)
                            }
                            className="w-full px-5 py-3.5 bg-slate-50 hover:bg-slate-100/80 flex items-center justify-between transition-colors border-b border-slate-100"
                          >
                            <div className="flex items-center gap-2">
                              <History size={16} className="text-[#2F5E2E]" />
                              <span className="text-xs font-black uppercase tracking-wider text-slate-800">
                                Revision Review History (
                                {reviewHistoryList.length})
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-slate-500 font-bold">
                              <span>
                                {isReviewHistoryOpen ? "Hide" : "Show"} Details
                              </span>
                              {isReviewHistoryOpen ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </div>
                          </button>

                          {isReviewHistoryOpen && (
                            <div className="p-4 space-y-3 bg-slate-50/40 divide-y divide-slate-100">
                              {reviewHistoryList.map(
                                (revLog: any, idx: number) => {
                                  const isApproved =
                                    revLog.decision === "Approved" ||
                                    revLog.status === "Approved";
                                  const isRejected =
                                    revLog.decision === "Rejected" ||
                                    revLog.status === "Rejected";

                                  return (
                                    <div
                                      key={revLog.reviewId || idx}
                                      className="pt-3 first:pt-0 space-y-2 text-xs"
                                    >
                                      <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-2">
                                          <span className="font-extrabold text-slate-800">
                                            Review #{revLog.reviewNo || idx + 1}
                                          </span>
                                          <span
                                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${isApproved
                                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                              : isRejected
                                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                                : "bg-amber-50 text-amber-700 border-amber-200"
                                              }`}
                                          >
                                            {revLog.decision || revLog.status}
                                          </span>
                                        </div>

                                        <span className="text-[11px] font-semibold text-slate-400">
                                          {new Date(
                                            revLog.reviewedOn,
                                          ).toLocaleString()}
                                        </span>
                                      </div>

                                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white p-2.5 rounded-xl border border-slate-200/80 font-medium text-slate-700 text-[11px]">
                                        <div>
                                          Original:{" "}
                                          <span className="line-through text-slate-400">
                                            AED{" "}
                                            {revLog.originalTotal?.toFixed(2)}
                                          </span>
                                        </div>
                                        <div>
                                          Revised:{" "}
                                          <strong className="text-slate-900">
                                            AED{" "}
                                            {revLog.revisedTotal?.toFixed(2)}
                                          </strong>
                                        </div>
                                        <div>
                                          Difference:{" "}
                                          <strong className="text-blue-700">
                                            AED{" "}
                                            {revLog.differenceAmount?.toFixed(
                                              2,
                                            )}
                                          </strong>
                                        </div>
                                        <div>
                                          Reviewer:{" "}
                                          <strong>
                                            {revLog.reviewedBy || "System"}
                                          </strong>
                                        </div>
                                      </div>

                                      {revLog.remarks && (
                                        <p className="text-[11px] text-slate-600 bg-white p-2 rounded-lg border border-slate-100 italic">
                                          Staff Notes: "{revLog.remarks.trim()}"
                                        </p>
                                      )}

                                      {revLog.customerRemarks && (
                                        <p className="text-[11px] text-blue-700 bg-blue-50/60 p-2 rounded-lg border border-blue-100 italic">
                                          Customer Response: "
                                          {revLog.customerRemarks.trim()}"
                                        </p>
                                      )}
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Order Activity Timeline (Sorted Newest-First) */}
                  {activeTab === "activity" && (
                    <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm space-y-6">
                      {isActivityLoading ? (
                        <div className="flex justify-center p-6">
                          <RefreshCw
                            className="animate-spin text-slate-400"
                            size={24}
                          />
                        </div>
                      ) : activitiesList && activitiesList.length > 0 ? (
                        <div className="relative space-y-6">
                          {activitiesList.map(
                            (act: orderActivity, idx: number) => {
                              const badge = getActivityTypeBadge(
                                act.activityType,
                              );

                              return (
                                <div
                                  key={act.activityId || idx}
                                  className="flex gap-4 relative"
                                >
                                  <div className="flex flex-col items-center">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white border border-slate-200 shadow-xs z-10 shrink-0">
                                      {badge.icon}
                                    </div>
                                    {idx !== activitiesList.length - 1 && (
                                      <div className="w-0.5 flex-1 bg-slate-100 my-1" />
                                    )}
                                  </div>

                                  <div className="flex-1 bg-slate-50/70 p-4 rounded-2xl border border-slate-200/70 space-y-3">
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                      <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <h4 className="text-xs font-extrabold text-slate-900">
                                            {act.title}
                                          </h4>
                                          <span
                                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${badge.bg}`}
                                          >
                                            {act.activityType}
                                          </span>
                                        </div>
                                        <p className="text-xs text-slate-600 mt-1">
                                          {act.description}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-[11px] font-semibold text-slate-400">
                                          {new Date(
                                            act.createdOn,
                                          ).toLocaleString()}
                                        </p>
                                        <span className="text-[10px] font-bold text-slate-500 bg-slate-200/60 px-2 py-0.5 rounded-md inline-block mt-0.5">
                                          By: {act.performedBy} ({act.userRole})
                                        </span>
                                      </div>
                                    </div>

                                    {/* Payment Metadata */}
                                    {act.payment && (
                                      <div className="bg-white rounded-xl border border-emerald-200 p-3 space-y-1.5 text-xs text-slate-700">
                                        <div className="flex justify-between items-center font-bold text-emerald-800 border-b border-emerald-100 pb-1.5">
                                          <span>
                                            {act.payment.transactionType}
                                          </span>
                                          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px]">
                                            {act.payment.status}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pt-1 font-medium">
                                          <div>
                                            Amount:{" "}
                                            <strong className="text-emerald-700">
                                              {act.payment.currency}{" "}
                                              {act.payment.amount.toFixed(2)}
                                            </strong>
                                          </div>
                                          <div>
                                            Gateway:{" "}
                                            <strong>
                                              {act.payment.paymentGateway}
                                            </strong>
                                          </div>
                                          <div>
                                            Method:{" "}
                                            <strong>
                                              {act.payment.paymentMethod}
                                            </strong>
                                          </div>
                                          {act.payment.transactionReference && (
                                            <div className="col-span-2 truncate text-[11px] text-slate-400">
                                              Ref:{" "}
                                              {act.payment.transactionReference}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Refund Metadata */}
                                    {act.refund && (
                                      <div className="bg-white rounded-xl border border-rose-200 p-3 space-y-1.5 text-xs text-slate-700">
                                        <div className="flex justify-between items-center font-bold text-rose-800 border-b border-rose-100 pb-1.5">
                                          <span>Refund Details</span>
                                          <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px]">
                                            {act.refund.status}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pt-1 font-medium">
                                          <div>
                                            Amount:{" "}
                                            <strong className="text-rose-600">
                                              AED {act.refund.amount.toFixed(2)}
                                            </strong>
                                          </div>
                                          <div>
                                            Method:{" "}
                                            <strong>
                                              {act.refund.refundMethod}
                                            </strong>
                                          </div>
                                          <div className="col-span-2 italic text-slate-500">
                                            Reason: "{act.refund.reason}"
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Shipment Metadata */}
                                    {act.shipment && (
                                      <div className="bg-white rounded-xl border border-blue-200 p-3 space-y-2 text-xs text-slate-700">
                                        <div className="flex justify-between items-center font-bold text-blue-900 border-b border-blue-100 pb-1.5">
                                          <span>
                                            Shipment Partner:{" "}
                                            {act.shipment.provider}
                                          </span>
                                          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] uppercase">
                                            {act.shipment.status}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 font-medium">
                                          {act.shipment.courierName && (
                                            <div>
                                              Courier:{" "}
                                              <strong>
                                                {act.shipment.courierName}
                                              </strong>
                                            </div>
                                          )}
                                          {act.shipment.trackingNumber && (
                                            <div>
                                              Tracking #:{" "}
                                              <strong className="font-mono">
                                                {act.shipment.trackingNumber}
                                              </strong>
                                            </div>
                                          )}
                                          {act.shipment.driverName && (
                                            <div className="flex items-center gap-1">
                                              <UserCheck
                                                size={12}
                                                className="text-slate-400"
                                              />
                                              <span>
                                                Driver:{" "}
                                                <strong>
                                                  {act.shipment.driverName}
                                                </strong>
                                              </span>
                                            </div>
                                          )}
                                          {act.shipment.driverPhoneNumber && (
                                            <div className="flex items-center gap-1">
                                              <Phone
                                                size={12}
                                                className="text-slate-400"
                                              />
                                              <span>
                                                Ph:{" "}
                                                <strong>
                                                  {
                                                    act.shipment
                                                      .driverPhoneNumber
                                                  }
                                                </strong>
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Revision Metadata & Item Image Renderers */}
                                    {act.revision && (
                                      <div className="bg-white rounded-xl border border-amber-200 p-3 space-y-3 text-xs">
                                        <div className="flex justify-between items-center font-bold text-amber-900 border-b border-amber-100 pb-2">
                                          <span>
                                            Revision #{act.revision.revisionNo}
                                          </span>
                                          <div className="flex gap-1.5">
                                            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px]">
                                              Status: {act.revision.status}
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px]">
                                              Pay: {act.revision.paymentStatus}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2 rounded-lg font-medium text-slate-700">
                                          <div>
                                            Original:{" "}
                                            <span className="line-through text-slate-400">
                                              AED{" "}
                                              {act.revision.originalTotal.toFixed(
                                                2,
                                              )}
                                            </span>
                                          </div>
                                          <div>
                                            Revised:{" "}
                                            <strong className="text-slate-900">
                                              AED{" "}
                                              {act.revision.revisedTotal.toFixed(
                                                2,
                                              )}
                                            </strong>
                                          </div>
                                          <div>
                                            Difference:{" "}
                                            <strong className="text-blue-700">
                                              AED{" "}
                                              {act.revision.differenceAmount.toFixed(
                                                2,
                                              )}
                                            </strong>
                                          </div>
                                        </div>

                                        {/* Revision Product Snapshots with Images */}
                                        {act.revision.items &&
                                          act.revision.items.length > 0 && (
                                            <div className="space-y-2 pt-1">
                                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                Item Changes (
                                                {act.revision.items.length})
                                              </p>
                                              {act.revision.items.map(
                                                (revItem, ri) => {
                                                  const snapshot =
                                                    revItem.newSnapshot ||
                                                    revItem.oldSnapshot;
                                                  const imageUrl =
                                                    snapshot?.imageUrl || null;

                                                  return (
                                                    <div
                                                      key={
                                                        revItem.revisionItemId ||
                                                        ri
                                                      }
                                                      className="p-2.5 rounded-lg border border-slate-200 bg-slate-50/50 flex items-center justify-between gap-3"
                                                    >
                                                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                        {imageUrl ? (
                                                          <img
                                                            src={imageUrl}
                                                            alt={
                                                              snapshot?.productName ||
                                                              "Product"
                                                            }
                                                            className="w-10 h-10 object-cover rounded-lg border border-slate-200 shrink-0"
                                                          />
                                                        ) : (
                                                          <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center text-slate-400 text-[9px] font-bold shrink-0">
                                                            No Img
                                                          </div>
                                                        )}

                                                        <div className="min-w-0 flex-1">
                                                          <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span
                                                              className={`px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase shrink-0 ${revItem.action ===
                                                                "RemoveProduct"
                                                                ? "bg-red-100 text-red-700"
                                                                : "bg-emerald-100 text-emerald-700"
                                                                }`}
                                                            >
                                                              {revItem.action}
                                                            </span>
                                                            <p className="font-bold text-slate-800 truncate text-[11px]">
                                                              {
                                                                snapshot?.productName
                                                              }
                                                            </p>
                                                          </div>
                                                          {revItem.reason && (
                                                            <p className="text-[10px] text-slate-400 italic mt-0.5">
                                                              "{revItem.reason}"
                                                            </p>
                                                          )}
                                                        </div>
                                                      </div>

                                                      <div className="text-right shrink-0">
                                                        <p className="font-black text-slate-800 text-[11px]">
                                                          AED{" "}
                                                          {(
                                                            snapshot?.totalPrice ||
                                                            0
                                                          ).toFixed(2)}
                                                        </p>
                                                        {snapshot?.quantity && (
                                                          <p className="text-[10px] text-slate-400 font-semibold">
                                                            Qty:{" "}
                                                            {snapshot.quantity}
                                                          </p>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                },
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    )}

                                    {/* Review Metadata */}
                                    {act.review && (
                                      <div className="bg-white rounded-xl border border-indigo-200 p-3 space-y-1.5 text-xs text-slate-700">
                                        <div className="flex justify-between items-center font-bold text-indigo-900 border-b border-indigo-100 pb-1.5">
                                          <span>
                                            Review Decision:{" "}
                                            {act.review.decision}
                                          </span>
                                          <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px]">
                                            {act.review.status}
                                          </span>
                                        </div>
                                        {act.review.remarks && (
                                          <p className="italic text-slate-600 bg-slate-50 p-2 rounded">
                                            Admin Remarks: "{act.review.remarks}
                                            "
                                          </p>
                                        )}
                                        {act.review.customerRemarks && (
                                          <p className="italic text-blue-600 bg-blue-50/50 p-2 rounded">
                                            Customer Remarks: "
                                            {act.review.customerRemarks}"
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    {/* Timeline Metadata */}
                                    {act.timeline && (
                                      <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2 text-xs text-slate-700">
                                        <div className="flex items-center justify-between font-bold text-slate-800 border-b border-slate-100 pb-2">
                                          <span className="flex items-center gap-2">
                                            <Clock size={13} className="text-slate-500" />
                                            Timeline Details
                                          </span>

                                          {act.timeline.paymentTransactionId && (
                                            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                                              Transaction #{act.timeline.paymentTransactionId}
                                            </span>
                                          )}
                                        </div>

                                        {act.timeline.oldValue !== null &&
                                          act.timeline.oldValue !== undefined && (
                                            <div className="flex justify-between gap-3">
                                              <span className="font-semibold text-slate-500">
                                                Previous Value
                                              </span>
                                              <span className="font-bold text-slate-700">
                                                {String(act.timeline.oldValue)}
                                              </span>
                                            </div>
                                          )}

                                        {act.timeline.newValue !== null &&
                                          act.timeline.newValue !== undefined && (
                                            <div className="flex justify-between gap-3">
                                              <span className="font-semibold text-slate-500">
                                                New Value
                                              </span>
                                              <span className="font-bold text-slate-700">
                                                {String(act.timeline.newValue)}
                                              </span>
                                            </div>
                                          )}

                                        {act.timeline.additionalData && (
                                          <div className="bg-slate-50 rounded-lg p-2.5 space-y-1.5">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                              Additional Details
                                            </p>

                                            {(() => {
                                              let additionalData: Record<string, any> = {};

                                              try {
                                                additionalData =
                                                  typeof act.timeline.additionalData === "string"
                                                    ? JSON.parse(act.timeline.additionalData)
                                                    : act.timeline.additionalData || {};
                                              } catch {
                                                return (
                                                  <p className="text-[11px] text-slate-600 break-all">
                                                    {String(act.timeline.additionalData)}
                                                  </p>
                                                );
                                              }

                                              return (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                  {Object.entries(additionalData).map(([key, value]) => (
                                                    <div
                                                      key={key}
                                                      className="flex justify-between gap-3 bg-white rounded-md px-2 py-1.5 border border-slate-100"
                                                    >
                                                      <span className="font-semibold text-slate-500">
                                                        {key}
                                                      </span>

                                                      <span className="font-bold text-slate-700 text-right break-all">
                                                        {value === null || value === undefined
                                                          ? "-"
                                                          : typeof value === "object"
                                                            ? JSON.stringify(value)
                                                            : String(value)}
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 text-center py-6">
                          No order activities logged yet.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Refunds Tab */}
                  {activeTab === "refunds" && (
                    <div className="space-y-4">
                      <form
                        onSubmit={handleProcessCustomRefund}
                        className="bg-emerald-50/40 rounded-2xl p-5 border border-emerald-100 space-y-4"
                      >
                        <h4 className="text-xs font-bold text-emerald-900 uppercase tracking-wider flex items-center gap-2">
                          <AlertCircle size={15} />
                          Process Custom Refund
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="relative">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                              AED
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={refundAmount}
                              onChange={(e) => setRefundAmount(e.target.value)}
                              className="w-full pl-12 pr-3 py-2.5 border border-emerald-200 rounded-xl text-sm bg-white font-mono font-bold text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <input
                              type="text"
                              placeholder="Reason for refund..."
                              value={refundReason}
                              onChange={(e) => setRefundReason(e.target.value)}
                              className="w-full px-4 py-2.5 border border-emerald-200 rounded-xl text-sm bg-white font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                          <button
                            type="button"
                            onClick={() => setSelectedRefundMethod("Wallet")}
                            className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all ${selectedRefundMethod === "Wallet"
                              ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-xs"
                              : "bg-white/70 border-slate-200 hover:border-slate-300"
                              }`}
                          >
                            <div
                              className={`p-1.5 rounded-lg shrink-0 ${selectedRefundMethod === "Wallet"
                                ? "bg-emerald-50 text-[#2F5E2E]"
                                : "bg-slate-100 text-slate-400"
                                }`}
                            >
                              <Wallet size={16} />
                            </div>
                            <span className="text-xs font-bold text-slate-800">
                              Customer Wallet
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setSelectedRefundMethod("OriginalPayment")
                            }
                            className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all ${selectedRefundMethod === "OriginalPayment"
                              ? "bg-white border-[#6DBE45] ring-2 ring-[#6DBE45]/20 shadow-xs"
                              : "bg-white/70 border-slate-200 hover:border-slate-300"
                              }`}
                          >
                            <div
                              className={`p-1.5 rounded-lg shrink-0 ${selectedRefundMethod === "OriginalPayment"
                                ? "bg-emerald-50 text-[#2F5E2E]"
                                : "bg-slate-100 text-slate-400"
                                }`}
                            >
                              <CreditCard size={16} />
                            </div>
                            <span className="text-xs font-bold text-slate-800">
                              Original Payment Source
                            </span>
                          </button>
                        </div>

                        <button
                          type="submit"
                          disabled={
                            isRefundSubmitting ||
                            !refundAmount ||
                            !refundReason.trim()
                          }
                          className="w-full py-2.5 bg-[#6DBE45] hover:bg-[#5FB63F] text-white font-bold text-xs rounded-xl transition-all duration-150 active:scale-95 disabled:bg-slate-200 disabled:text-slate-400 flex items-center justify-center gap-2 shadow-sm"
                        >
                          {isRefundSubmitting ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : (
                            <CheckCircle2 size={14} />
                          )}
                          Process Custom Refund
                        </button>
                      </form>

                      <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                        <AlertCircle
                          className="mx-auto mb-3 text-slate-400"
                          size={28}
                        />
                        <h4 className="text-sm font-semibold text-slate-700">
                          No Custom Refunds Logged
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          Processed refunds will reflect here and update the
                          history timeline.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Save Bar Layout */}
          {showSaveBar && (
            <div className="border-t border-slate-200/70 bg-white/95 backdrop-blur-md px-6 md:px-8 py-4 shadow-[0_-4px_16px_rgba(0,0,0,0.04)] shrink-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                    {acceptedCount} Accepted
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-50 text-red-700 text-[11px] font-bold border border-red-200">
                    {rejectedCount} Rejected
                  </span>
                </div>

                {derivedActionInfo.needsRemarks && (
                  <input
                    type="text"
                    placeholder="Remarks..."
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:outline-none focus:bg-white transition-colors"
                  />
                )}

                <button
                  onClick={handleSaveOrder}
                  disabled={isSubmittingReview}
                  className="sm:ml-auto px-6 py-2.5 font-bold text-sm text-white rounded-xl bg-[#6DBE45] hover:bg-[#5FB63F] shadow-sm flex items-center justify-center gap-2 shrink-0 transition-colors"
                >
                  {isSubmittingReview ? (
                    <RefreshCw size={15} className="animate-spin" />
                  ) : (
                    <Save size={15} />
                  )}
                  {derivedActionInfo.label}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {refundConfirmation && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-amber-100 bg-amber-50 flex items-start gap-3">
              <div className="p-2 rounded-xl bg-amber-100 text-amber-700 shrink-0">
                <AlertCircle size={20} />
              </div>

              <div>
                <h3 className="text-sm font-bold text-amber-900">
                  Refund Warning
                </h3>

                <p className="text-xs text-amber-700 mt-1">
                  Please review the refund information before continuing.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-slate-500">
                    Refund Amount
                  </span>

                  <span className="text-sm font-bold text-slate-900">
                    AED {refundConfirmation.amount.toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Please Review
                </p>

                <div className="space-y-2">
                  {refundConfirmation.warnings.map((warning, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg p-3"
                    >
                      <AlertCircle
                        size={14}
                        className="text-amber-600 mt-0.5 shrink-0"
                      />

                      <p className="text-xs text-amber-900 leading-relaxed">
                        {warning}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                The refund can still be processed if you confirm that you want
                to continue.
              </p>
            </div>

            {/* Actions */}
            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRefundConfirmation(null)}
                disabled={isRefundSubmitting}
                className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={isRefundSubmitting}
                onClick={async () => {
                  try {
                    const success = await processRefund(
                      refundConfirmation.amount,
                      refundConfirmation.reason,
                      true,
                    );

                    if (!success) {
                      return;
                    }

                    setRefundConfirmation(null);

                    setRefundAmount("");
                    setRefundReason("");

                    showNotification(
                      "Refund processed successfully.",
                      "success",
                    );

                    await triggerReviewsPending(orderId);
                    onRefreshDashboard();
                  } catch {
                    showNotification("Failed to process refund.", "error");
                  }
                }}
                className="px-4 py-2.5 rounded-xl bg-[#6DBE45] text-white text-xs font-bold hover:bg-[#5FB63F] disabled:opacity-50 flex items-center gap-2"
              >
                {isRefundSubmitting ? (
                  <>
                    <RefreshCw size={13} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={13} />
                    Continue Refund
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailDrawer;
